import React, { useEffect, useRef, useState } from 'react';
import { LogIn, LogOut, ChevronDown, CreditCard, Activity, Key, Receipt } from 'lucide-react';
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { getLngFromPathname } from './i18n';
import QueueModal from './QueueModal';

export default function App() {
  const { t, i18n } = useTranslation();
  const getHomePath = () => {
    const lng = getLngFromPathname(window.location.pathname);
    return lng ? `/${lng}/` : '/';
  };
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showSentPage, setShowSentPage] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [password2, setPassword2] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [usernamePolicyError, setUsernamePolicyError] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [plans, setPlans] = useState([]);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subStatus, setSubStatus] = useState('');
  const [subPlanCode, setSubPlanCode] = useState('');
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupStatus, setTopupStatus] = useState('');
  const [topupUnits, setTopupUnits] = useState(1);
  const [showSepModal, setShowSepModal] = useState(false);
  const [sepFile, setSepFile] = useState(null);
  const [sepError, setSepError] = useState('');
  const [sepDuration, setSepDuration] = useState(0);
  const [sepPoints, setSepPoints] = useState(0);
  const [sepUploadProgress, setSepUploadProgress] = useState(0);
  const [showSepSuccess, setShowSepSuccess] = useState(false);
  const [sepModel, setSepModel] = useState('normal');
  const [sepBusy, setSepBusy] = useState(false);
  const [showPic2danceModal, setShowPic2danceModal] = useState(false);
  const [pic2danceImageFile, setPic2danceImageFile] = useState(null);
  const [pic2danceVideoFile, setPic2danceVideoFile] = useState(null);
  const [pic2danceError, setPic2danceError] = useState('');
  const [pic2danceDuration, setPic2danceDuration] = useState(0);
  const [pic2dancePoints, setPic2dancePoints] = useState(0);
  const [pic2danceUploadProgress, setPic2danceUploadProgress] = useState(0);
  const [pic2danceBusy, setPic2danceBusy] = useState(false);
  const [showPic2danceSuccess, setShowPic2danceSuccess] = useState(false);
  const [pic2danceLongest, setPic2danceLongest] = useState('1080');
  const [pic2danceJobSerial, setPic2danceJobSerial] = useState(0);
  const [showDenoiseModal, setShowDenoiseModal] = useState(false);
  const [denoiseFile, setDenoiseFile] = useState(null);
  const [denoiseError, setDenoiseError] = useState('');
  const [denoiseDuration, setDenoiseDuration] = useState(0);
  const [denoisePoints, setDenoisePoints] = useState(0);
  const [denoiseUploadProgress, setDenoiseUploadProgress] = useState(0);
  const [showDenoiseSuccess, setShowDenoiseSuccess] = useState(false);
  const [denoiseBusy, setDenoiseBusy] = useState(false);
  const [showConvModal, setShowConvModal] = useState(false);
  const [convFile, setConvFile] = useState(null);
  const [convError, setConvError] = useState('');
  const [convDuration, setConvDuration] = useState(0);
  const [convPoints, setConvPoints] = useState(0);
  const [convPreset, setConvPreset] = useState('720');
  const [depthScale, setDepthScale] = useState(40);
  const [convUploadProgress, setConvUploadProgress] = useState(0);
  const [convBusy, setConvBusy] = useState(false);
  const [showFaceSwapModal, setShowFaceSwapModal] = useState(false);
  const [faceSwapVideoFile, setFaceSwapVideoFile] = useState(null);
  const [faceSwapImageFile, setFaceSwapImageFile] = useState(null);
  const [faceSwapError, setFaceSwapError] = useState('');
  const [faceSwapDuration, setFaceSwapDuration] = useState(0);
  const [faceSwapPoints, setFaceSwapPoints] = useState(0);
  const [faceSwapUploadProgress, setFaceSwapUploadProgress] = useState(0);
  const [faceSwapBusy, setFaceSwapBusy] = useState(false);
  const [showFaceSwapSuccess, setShowFaceSwapSuccess] = useState(false);
  const [faceSwapJobSerial, setFaceSwapJobSerial] = useState(0);
  const [faceSwapFps, setFaceSwapFps] = useState('30');
  useEffect(() => {
    if (convDuration <= 0) { setConvPoints(0); return; }
    const rate = convPreset === '1080' ? 12 : (convPreset === '720' ? 10 : 9);
    // Calculate points based on 10-second units
    const units = Math.ceil(convDuration / 10);
    setConvPoints(units * rate);
  }, [convPreset, convDuration]);
  useEffect(() => {
    if (pic2danceDuration <= 0) { setPic2dancePoints(0); return; }
    const longest = parseInt(String(pic2danceLongest || '0'), 10);
    const rate = longest >= 1080 ? 80 : (longest >= 720 ? 50 : 40);
    const sec = Math.ceil(pic2danceDuration);
    setPic2dancePoints(sec * rate);
  }, [pic2danceDuration, pic2danceLongest]);
  useEffect(() => {
    if (faceSwapDuration <= 0) { setFaceSwapPoints(0); return; }
    const rate = faceSwapFps === '30' ? 6 : 3;
    const sec = Math.ceil(faceSwapDuration);
    setFaceSwapPoints(sec * rate);
  }, [faceSwapDuration, faceSwapFps]);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [planChangeBusy, setPlanChangeBusy] = useState('');
  const [topupBusy, setTopupBusy] = useState(false);
  const [showPaypalRedirectModal, setShowPaypalRedirectModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [cpCurrentPassword, setCpCurrentPassword] = useState('');
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpNewPassword2, setCpNewPassword2] = useState('');
  const [cpPasswordMismatch, setCpPasswordMismatch] = useState(false);
  const [cpPasswordPolicyError, setCpPasswordPolicyError] = useState('');
  const [cpMessage, setCpMessage] = useState('');
  const [cpBusy, setCpBusy] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSupportSentModal, setShowSupportSentModal] = useState(false);
  const [supportCategory, setSupportCategory] = useState('');
  const [supportQuery, setSupportQuery] = useState('');
  const [supportPrivacyOk, setSupportPrivacyOk] = useState(false);
  const [supportAttachFiles, setSupportAttachFiles] = useState([]);
  const [supportAttachError, setSupportAttachError] = useState('');
  const [supportSendBusy, setSupportSendBusy] = useState(false);
  const [supportSendError, setSupportSendError] = useState('');
  const [supportSentUuid, setSupportSentUuid] = useState('');
  const [supportEnvOs, setSupportEnvOs] = useState('');
  const [supportEnvBrowser, setSupportEnvBrowser] = useState('');
  const supportFileInputRef = useRef(null);
  const sepFileInputRef = useRef(null);
  const pic2danceImageInputRef = useRef(null);
  const pic2danceVideoInputRef = useRef(null);
  const faceSwapVideoInputRef = useRef(null);
  const faceSwapImageInputRef = useRef(null);
  const denoiseFileInputRef = useRef(null);
  const convFileInputRef = useRef(null);
  const mediaInfoInstanceRef = useRef(null);
  const mediaInfoLoadPromiseRef = useRef(null);
  const convAnalyzeSeqRef = useRef(0);
  const denoiseAnalyzeSeqRef = useRef(0);
  const pic2danceAnalyzeSeqRef = useRef(0);
  const faceSwapAnalyzeSeqRef = useRef(0);
  
  // API base: prefer SERVER_URL from .env, else default to current host with port 4000
  const API_BASE = String(process.env.REACT_APP_SERVER_URL || process.env.SERVER_URL || '').trim() || `${window.location.protocol}//${window.location.hostname}:4000`;
  const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;
  const MAX_UPLOAD_ERROR = t('errors.maxUpload');

  const USERNAME_POLICY_TEXT = t('auth.usernamePolicyText');
  const PASSWORD_POLICY_TEXT = t('auth.passwordPolicyText');
  const SIGNUP_POLICY_TEXT = `${USERNAME_POLICY_TEXT}${PASSWORD_POLICY_TEXT}`;
  const USERNAME_AVAILABLE_MESSAGE = t('auth.usernameAvailable');
  const USERNAME_TAKEN_MESSAGE = t('auth.usernameTaken');
  const USERNAME_RE = /^[A-Za-z0-9]{5,15}$/;
  const PASSWORD_SYMBOL_RE = /[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/;
  const isValidUsername = (u) => {
    if (typeof u !== 'string') return false;
    return USERNAME_RE.test(u);
  };
  const isStrongPassword = (pw) => {
    if (typeof pw !== 'string') return false;
    if (pw.length < 8 || pw.length > 31) return false;
    if (!/[A-Z]/.test(pw)) return false;
    if (!/[a-z]/.test(pw)) return false;
    if (!/[0-9]/.test(pw)) return false;
    if (!PASSWORD_SYMBOL_RE.test(pw)) return false;
    return true;
  };

  const userPoints = Number(user?.points || 0) || 0;
  const sepInsufficient = !!user && sepPoints > 0 && userPoints < sepPoints;
  const pic2danceInsufficient = !!user && pic2dancePoints > 0 && userPoints < pic2dancePoints;
  const denoiseInsufficient = !!user && denoisePoints > 0 && userPoints < denoisePoints;
  const convInsufficient = !!user && convPoints > 0 && userPoints < convPoints;

  const resetSepState = () => {
    setSepFile(null);
    setSepError('');
    setSepDuration(0);
    setSepPoints(0);
    setSepUploadProgress(0);
    setSepBusy(false);
    if (sepFileInputRef.current) sepFileInputRef.current.value = '';
  };

  const resetDenoiseState = () => {
    setDenoiseFile(null);
    setDenoiseError('');
    setDenoiseDuration(0);
    setDenoisePoints(0);
    setDenoiseUploadProgress(0);
    setDenoiseBusy(false);
    if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = '';
  };

  const resetPic2danceState = () => {
    setPic2danceImageFile(null);
    setPic2danceVideoFile(null);
    setPic2danceError('');
    setPic2danceDuration(0);
    setPic2dancePoints(0);
    setPic2danceUploadProgress(0);
    setPic2danceBusy(false);
    setPic2danceJobSerial(0);
    setPic2danceLongest('1080');
    if (pic2danceImageInputRef.current) pic2danceImageInputRef.current.value = '';
    if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.value = '';
  };

  const resetFaceSwapState = () => {
    setFaceSwapVideoFile(null);
    setFaceSwapImageFile(null);
    setFaceSwapError('');
    setFaceSwapDuration(0);
    setFaceSwapPoints(0);
    setFaceSwapUploadProgress(0);
    setFaceSwapBusy(false);
    setFaceSwapJobSerial(0);
    setFaceSwapFps('30');
    if (faceSwapVideoInputRef.current) faceSwapVideoInputRef.current.value = '';
    if (faceSwapImageInputRef.current) faceSwapImageInputRef.current.value = '';
  };

  const loadMediaInfoInstance = () => {
    if (mediaInfoInstanceRef.current) return Promise.resolve(mediaInfoInstanceRef.current);
    if (mediaInfoLoadPromiseRef.current) return mediaInfoLoadPromiseRef.current;

    const MEDIAINFO_VERSION = '0.1.7';
    const MEDIAINFO_DIST = `https://unpkg.com/mediainfo.js@${MEDIAINFO_VERSION}/dist`;

    const ensureScript = () => new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && typeof window.MediaInfo === 'function') { resolve(null); return; }
      const existing = document.querySelector('script[data-mediainfo="1"]');
      if (existing) {
        if (existing.dataset.loaded === '1' || (typeof window !== 'undefined' && typeof window.MediaInfo === 'function')) { resolve(null); return; }
        existing.addEventListener('load', () => resolve(null), { once: true });
        existing.addEventListener('error', () => reject(new Error('mediainfo load failed')), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = `${MEDIAINFO_DIST}/mediainfo.min.js`;
      s.async = true;
      s.defer = true;
      s.dataset.mediainfo = '1';
      s.onload = () => { s.dataset.loaded = '1'; resolve(null); };
      s.onerror = () => reject(new Error('mediainfo load failed'));
      document.head.appendChild(s);
    });

    const createInstance = () => new Promise((resolve, reject) => {
      const fn = (typeof window !== 'undefined') ? window.MediaInfo : null;
      if (typeof fn !== 'function') { reject(new Error('mediainfo not available')); return; }
      let settled = false;
      const onResolve = (mi) => { if (settled) return; settled = true; resolve(mi); };
      const onReject = (err) => { if (settled) return; settled = true; reject(err instanceof Error ? err : new Error('mediainfo init failed')); };
      const ret = fn(
        {
          format: 'object',
          locateFile: (url) => {
            const clean = String(url || '').replace(/^\/+/, '');
            if (clean.toLowerCase().endsWith('.wasm')) return `${MEDIAINFO_DIST}/${clean}`;
            return `${MEDIAINFO_DIST}/${clean}`;
          }
        },
        onResolve,
        onReject
      );
      if (ret && typeof ret.then === 'function') ret.then(onResolve, onReject);
    });

    mediaInfoLoadPromiseRef.current = ensureScript()
      .then(() => createInstance())
      .then((mi) => {
        mediaInfoInstanceRef.current = mi;
        return mi;
      });

    return mediaInfoLoadPromiseRef.current;
  };

  const parseIntLoose = (v) => {
    const m = String(v ?? '').match(/[0-9]+/);
    const n = m ? parseInt(m[0], 10) : 0;
    return Number.isFinite(n) ? n : 0;
  };

  const parseDurationToSeconds = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return 0;

    if (/[a-z]/i.test(s)) {
      let total = 0;
      const h = s.match(/(\d+(?:\.\d+)?)\s*h/i);
      const m = s.match(/(\d+(?:\.\d+)?)\s*(?:min|mn)\b/i);
      const sec = s.match(/(\d+(?:\.\d+)?)\s*(?:s|sec)\b/i);
      const ms = s.match(/(\d+(?:\.\d+)?)\s*ms/i);
      if (h) total += Number(h[1]) * 3600;
      if (m) total += Number(m[1]) * 60;
      if (sec) total += Number(sec[1]);
      if (ms) total += Number(ms[1]) / 1000;
      if (Number.isFinite(total) && total > 0) return Math.round(total);
    }

    const num = Number(s.replace(/,/g, ''));
    if (Number.isFinite(num) && num > 0) {
      return Math.round(num > 10000 ? (num / 1000) : num);
    }

    return 0;
  };

  const extractVideoMetaFromMediaInfo = (result) => {
    const tracks = (result && result.media && Array.isArray(result.media.track)) ? result.media.track : [];
    const general = tracks.find(t => String(((t && t['@type']) || '')) === 'General') || null;
    const video = tracks.find(t => String(((t && t['@type']) || '')) === 'Video') || null;

    const width = parseIntLoose(video ? video.Width : 0);
    const height = parseIntLoose(video ? video.Height : 0);

    const durationSec = Math.max(
      0,
      parseDurationToSeconds(general ? general.Duration : null) ||
        parseDurationToSeconds(video ? video.Duration : null) ||
        parseDurationToSeconds(general ? general['Duration/String3'] : null) ||
        parseDurationToSeconds(general ? general['Duration/String2'] : null) ||
        parseDurationToSeconds(video ? video['Duration/String3'] : null) ||
        parseDurationToSeconds(video ? video['Duration/String2'] : null)
    );
    return { durationSec, width, height };
  };

  const analyzeVideoMetaWithMediaInfo = (file) => {
    return loadMediaInfoInstance()
      .then((mi) => mi.analyzeData(
        file.size,
        (chunkSize, offset) => file.slice(offset, offset + chunkSize).arrayBuffer().then((buf) => new Uint8Array(buf))
      ).catch((e) => { throw (e instanceof Error ? e : new Error('mediainfo analyze failed')); }))
      .then((result) => extractVideoMetaFromMediaInfo(result));
  };

  const analyzeVideoMetaWithVideoElement = (file) => {
    const url = URL.createObjectURL(file);
    return new Promise((resolve, reject) => {
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.src = url;
      el.onloadedmetadata = () => {
        const dur = Math.max(0, Math.round(el.duration || 0));
        const width = Number(el.videoWidth || 0);
        const height = Number(el.videoHeight || 0);
        URL.revokeObjectURL(url);
        resolve({ durationSec: dur, width, height });
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('metadata error'));
      };
    });
  };

  const readFileRange = (file, start, length) => {
    const s = Math.max(0, Number(start || 0) || 0);
    const len = Math.max(0, Number(length || 0) || 0);
    return file.slice(s, s + len).arrayBuffer().then((buf) => new Uint8Array(buf));
  };

  const u32le = (dv, offset) => dv.getUint32(offset, true);

  const analyzeAviMetaFromHead = (head) => {
    if (!head || head.length < 16) return null;
    if (String.fromCharCode(head[0], head[1], head[2], head[3]) !== 'RIFF') return null;
    if (String.fromCharCode(head[8], head[9], head[10], head[11]) !== 'AVI ') return null;

    const dv = new DataView(head.buffer, head.byteOffset, head.byteLength);
    const sig = [0x61, 0x76, 0x69, 0x68];
    for (let i = 0; i + 8 + 56 <= head.length; i += 1) {
      if (head[i] !== sig[0] || head[i + 1] !== sig[1] || head[i + 2] !== sig[2] || head[i + 3] !== sig[3]) continue;
      const chunkSize = u32le(dv, i + 4);
      if (chunkSize < 56) continue;
      const dataOff = i + 8;
      if (dataOff + 40 > head.length) continue;
      const dwMicroSecPerFrame = u32le(dv, dataOff + 0);
      const dwTotalFrames = u32le(dv, dataOff + 16);
      const width = u32le(dv, dataOff + 32);
      const height = u32le(dv, dataOff + 36);
      const dur = (Number(dwMicroSecPerFrame || 0) > 0 && Number(dwTotalFrames || 0) > 0)
        ? Math.round((Number(dwMicroSecPerFrame) * Number(dwTotalFrames)) / 1000000)
        : 0;
      return { durationSec: Math.max(0, dur), width: Number(width || 0), height: Number(height || 0) };
    }
    return null;
  };

  const parseScrBaseFromPack = (buf, startOffset) => {
    let bytePos = startOffset;
    let bitPos = 0;
    const readBits = (n) => {
      let v = 0;
      for (let i = 0; i < n; i += 1) {
        if (bytePos >= buf.length) return null;
        const bit = (buf[bytePos] >> (7 - bitPos)) & 1;
        v = (v * 2) + bit;
        bitPos += 1;
        if (bitPos >= 8) { bitPos = 0; bytePos += 1; }
      }
      return v;
    };

    const first2 = readBits(2);
    if (first2 === null) return null;
    if (first2 === 1) {
      const scr32_30 = readBits(3); if (scr32_30 === null) return null;
      const mk1 = readBits(1); if (mk1 !== 1) return null;
      const scr29_15 = readBits(15); if (scr29_15 === null) return null;
      const mk2 = readBits(1); if (mk2 !== 1) return null;
      const scr14_0 = readBits(15); if (scr14_0 === null) return null;
      const mk3 = readBits(1); if (mk3 !== 1) return null;
      return (scr32_30 * (2 ** 30)) + (scr29_15 * (2 ** 15)) + scr14_0;
    }

    const next2 = readBits(2);
    if (next2 === null) return null;
    if (((first2 * 4) + next2) !== 2) return null;
    const scr32_30 = readBits(3); if (scr32_30 === null) return null;
    const mk1 = readBits(1); if (mk1 !== 1) return null;
    const scr29_15 = readBits(15); if (scr29_15 === null) return null;
    const mk2 = readBits(1); if (mk2 !== 1) return null;
    const scr14_0 = readBits(15); if (scr14_0 === null) return null;
    const mk3 = readBits(1); if (mk3 !== 1) return null;
    return (scr32_30 * (2 ** 30)) + (scr29_15 * (2 ** 15)) + scr14_0;
  };

  const extractMpegSequenceSizeFromHead = (head) => {
    if (!head || head.length < 16) return { width: 0, height: 0 };
    for (let i = 0; i + 8 < head.length; i += 1) {
      if (head[i] !== 0x00 || head[i + 1] !== 0x00 || head[i + 2] !== 0x01 || head[i + 3] !== 0xB3) continue;
      const b0 = head[i + 4];
      const b1 = head[i + 5];
      const b2 = head[i + 6];
      const width = ((b0 << 4) | (b1 >> 4)) & 0x0FFF;
      const height = (((b1 & 0x0F) << 8) | b2) & 0x0FFF;
      return { width, height };
    }
    return { width: 0, height: 0 };
  };

  const extractMpegScrBaseFromHead = (head) => {
    if (!head || head.length < 20) return null;
    for (let i = 0; i + 14 < head.length; i += 1) {
      if (head[i] !== 0x00 || head[i + 1] !== 0x00 || head[i + 2] !== 0x01 || head[i + 3] !== 0xBA) continue;
      const base = parseScrBaseFromPack(head, i + 4);
      if (base !== null) return base;
    }
    return null;
  };

  const extractMpegScrBaseFromTail = (tail) => {
    if (!tail || tail.length < 20) return null;
    for (let i = tail.length - 20; i >= 0; i -= 1) {
      if (tail[i] !== 0x00 || tail[i + 1] !== 0x00 || tail[i + 2] !== 0x01 || tail[i + 3] !== 0xBA) continue;
      const base = parseScrBaseFromPack(tail, i + 4);
      if (base !== null) return base;
    }
    return null;
  };

  const findTsAlignment = (buf) => {
    if (!buf || buf.length < 188 * 5) return null;
    const candidates = [188, 192, 204];
    let best = null;
    for (const size of candidates) {
      const maxOffset = Math.min(size - 1, 1024);
      for (let offset = 0; offset <= maxOffset; offset += 1) {
        let ok = 0;
        for (let i = 0; i < 6; i += 1) {
          const off = offset + (i * size);
          if (off >= buf.length) break;
          if (buf[off] === 0x47) ok += 1;
        }
        if (!best || ok > best.ok) best = { packetSize: size, startOffset: offset, ok };
        if (ok >= 6) return { packetSize: size, startOffset: offset };
      }
    }
    if (best && best.ok >= 4) return { packetSize: best.packetSize, startOffset: best.startOffset };
    return null;
  };

  const parsePcrBaseFromTsPacket = (pkt) => {
    if (!pkt || pkt.length < 188) return null;
    if (pkt[0] !== 0x47) return null;
    const adaptationFieldControl = (pkt[3] >> 4) & 0x03;
    if (adaptationFieldControl !== 2 && adaptationFieldControl !== 3) return null;
    const afl = pkt[4];
    if (afl < 7) return null;
    const flags = pkt[5];
    if ((flags & 0x10) === 0) return null;
    const b6 = pkt[6];
    const b7 = pkt[7];
    const b8 = pkt[8];
    const b9 = pkt[9];
    const b10 = pkt[10];
    const base = (b6 * (2 ** 25)) + (b7 * (2 ** 17)) + (b8 * (2 ** 9)) + (b9 * (2 ** 1)) + (b10 >> 7);
    if (!Number.isFinite(base)) return null;
    return base;
  };

  const extractTsPcrBaseFromHead = (head) => {
    if (!head || head.length < 188 * 5) return null;
    const align = findTsAlignment(head);
    if (!align) return null;
    const { packetSize, startOffset } = align;
    const maxPackets = Math.floor((head.length - startOffset) / packetSize);
    for (let i = 0; i < maxPackets; i += 1) {
      const off = startOffset + (i * packetSize);
      if (head[off] !== 0x47) continue;
      const pkt = head.slice(off, off + 188);
      const pcr = parsePcrBaseFromTsPacket(pkt);
      if (pcr !== null) return pcr;
    }
    return null;
  };

  const extractTsPcrBaseFromTail = (tail) => {
    if (!tail || tail.length < 188 * 5) return null;
    const align = findTsAlignment(tail);
    if (!align) return null;
    const { packetSize, startOffset } = align;
    const maxPackets = Math.floor((tail.length - startOffset) / packetSize);
    for (let i = maxPackets - 1; i >= 0; i -= 1) {
      const off = startOffset + (i * packetSize);
      if (tail[off] !== 0x47) continue;
      const pkt = tail.slice(off, off + 188);
      const pcr = parsePcrBaseFromTsPacket(pkt);
      if (pcr !== null) return pcr;
    }
    return null;
  };

  const analyzeVideoMetaByContainerSniffing = (file) => {
    const nameLower = String(file?.name || '').toLowerCase();
    const ext = nameLower.includes('.') ? nameLower.slice(nameLower.lastIndexOf('.')) : '';
    if (ext === '.avi') {
      return readFileRange(file, 0, Math.min(1024 * 1024, Number(file.size || 0) || 0))
        .then((head) => {
          const meta = analyzeAviMetaFromHead(head);
          if (!meta || !meta.durationSec) throw new Error('avi meta missing');
          return meta;
        });
    }
    if (ext === '.mpg' || ext === '.mpeg' || ext === '.vob') {
      const size = Number(file.size || 0) || 0;
      const headSize = Math.min(2 * 1024 * 1024, size);
      const tailSize = Math.min(2 * 1024 * 1024, size);
      return Promise.all([
        readFileRange(file, 0, headSize),
        readFileRange(file, Math.max(0, size - tailSize), tailSize),
      ]).then(([head, tail]) => {
        const firstBase = extractMpegScrBaseFromHead(head);
        const lastBase = extractMpegScrBaseFromTail(tail);
        const seq = extractMpegSequenceSizeFromHead(head);
        if (firstBase === null || lastBase === null) throw new Error('mpeg scr missing');
        let diff = lastBase - firstBase;
        const mod = 2 ** 33;
        if (diff < 0) diff += mod;
        const dur = Math.round(diff / 90000);
        if (!Number.isFinite(dur) || dur <= 0) throw new Error('mpeg duration invalid');
        return { durationSec: dur, width: Number(seq.width || 0), height: Number(seq.height || 0) };
      });
    }
    if (ext === '.ts') {
      const size = Number(file.size || 0) || 0;
      const headSize = Math.min(8 * 1024 * 1024, size);
      const tailSize = Math.min(16 * 1024 * 1024, size);
      return Promise.all([
        readFileRange(file, 0, headSize),
        readFileRange(file, Math.max(0, size - tailSize), tailSize),
      ]).then(([head, tail]) => {
        const firstPcr = extractTsPcrBaseFromHead(head);
        const lastPcr = extractTsPcrBaseFromTail(tail);
        const seq = extractMpegSequenceSizeFromHead(head);
        if (firstPcr === null || lastPcr === null) throw new Error('ts pcr missing');
        let diff = lastPcr - firstPcr;
        const mod = 2 ** 33;
        if (diff < 0) diff += mod;
        const dur = Math.round(diff / 90000);
        if (!Number.isFinite(dur) || dur <= 0) throw new Error('ts duration invalid');
        return { durationSec: dur, width: Number(seq.width || 0), height: Number(seq.height || 0) };
      });
    }
    return Promise.reject(new Error('sniffing not supported'));
  };

  const resetConvState = () => {
    setConvFile(null);
    setConvError('');
    setConvDuration(0);
    setConvPoints(0);
    setConvUploadProgress(0);
    setConvBusy(false);
    if (convFileInputRef.current) convFileInputRef.current.value = '';
  };

  const parseUserAgent = () => {
    const ua = String(navigator.userAgent || '');
    let os = t('support.unknown');
    if (/Windows NT 11\.0/.test(ua) || /Windows 11/.test(ua)) os = 'Windows 11';
    else if (/Windows NT 10\.0/.test(ua) || /Windows 10/.test(ua)) os = 'Windows';
    else if (/Windows/.test(ua)) os = 'Windows';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/(iPhone|iPad|iPod)/.test(ua)) os = 'iOS / iPadOS';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Linux/.test(ua)) os = 'Linux';

    let browser = t('support.unknown');
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Safari';

    const verMatch = ua.match(/(Edg|Chrome|Firefox|Version)\/([0-9]+(\.[0-9]+)*)/);
    const ver = verMatch && verMatch[2] ? String(verMatch[2]) : '';
    const browserWithVer = ver ? `${browser} ${ver}` : browser;
    return { os, browser: browserWithVer };
  };

  const resetSupportState = () => {
    setSupportCategory('');
    setSupportQuery('');
    setSupportPrivacyOk(false);
    setSupportAttachFiles([]);
    setSupportAttachError('');
    setSupportSendBusy(false);
    setSupportSendError('');
    {
      const env = parseUserAgent();
      setSupportEnvOs(String(env.os || ''));
      setSupportEnvBrowser(String(env.browser || ''));
    }
    if (supportFileInputRef.current) supportFileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!showSupportModal) return;
    const env = parseUserAgent();
    setSupportEnvOs(String(env.os || ''));
    setSupportEnvBrowser(String(env.browser || ''));
    let cancelled = false;
    const uaData = navigator.userAgentData;
    if (uaData && uaData.platform === 'Windows' && typeof uaData.getHighEntropyValues === 'function') {
      uaData.getHighEntropyValues(['platformVersion', 'fullVersionList']).then((hev) => {
        if (cancelled) return;
        const pv = String(hev?.platformVersion || '');
        const major = parseInt((pv.split('.')[0] || '0'), 10);
        if (Number.isFinite(major) && major > 0) {
          setSupportEnvOs(major >= 13 ? 'Windows 11' : 'Windows 10');
        }
        const fvl = Array.isArray(hev?.fullVersionList) ? hev.fullVersionList : [];
        const edge = fvl.find(x => String(x?.brand || '').toLowerCase().includes('edge'));
        const chrome = fvl.find(x => String(x?.brand || '').toLowerCase().includes('chrome'));
        const chosen = edge || chrome || null;
        if (chosen && chosen.brand && chosen.version) {
          setSupportEnvBrowser(`${chosen.brand} ${chosen.version}`);
        }
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [showSupportModal]);

  const openFeature = (feature) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (feature === 'conv') {
      resetConvState();
      setShowConvModal(true);
      return;
    }
    if (feature === 'sep') {
      resetSepState();
      setShowSepModal(true);
      return;
    }
    if (feature === 'pic2dance') {
      resetPic2danceState();
      setShowPic2danceModal(true);
      return;
    }
    if (feature === 'denoise') {
      resetDenoiseState();
      setShowDenoiseModal(true);
      return;
    }
    if (feature === 'faceswap') {
      resetFaceSwapState();
      setShowFaceSwapModal(true);
      return;
    }
  };

  const supportCanSubmit = !!user && !!supportCategory && String(supportQuery || '').trim().length >= 4 && !!supportPrivacyOk && !supportSendBusy && !supportAttachError;

  const login = useGoogleLogin({
    ux_mode: 'popup',
    flow: 'implicit',
    scope: 'openid profile email',
    onSuccess: async (tokenResponse) => {
      try {
        console.log('Google tokenResponse', tokenResponse);
        console.log('Using API_BASE', API_BASE);
        const backend = await fetch(`${API_BASE}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken: tokenResponse.access_token }),
          credentials: 'include'
        });
        if (!backend.ok) {
          const errText = await backend.text().catch(() => '');
          console.error('Backend auth failed', backend.status, errText);
          throw new Error(`Backend auth failed (${backend.status})`);
        }
        const data = await backend.json();
        let nextUser = {
          name: data?.user?.name,
          email: data?.user?.email,
          picture: data?.user?.picture,
          signup_method: data?.user?.signup_method || 'google',
          points: data?.user?.points || 0,
          plan: data?.user?.plan || 'free',
        };
        try {
        const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (me.ok) {
          const meData = await me.json();
          nextUser.points = meData?.user?.points || 0;
          nextUser.plan = meData?.user?.plan || 'free';
          nextUser.signup_method = meData?.user?.signup_method || nextUser.signup_method;
        }
        } catch {}
        setUser(nextUser);
        setShowLoginModal(false);
      } catch (e) {
        console.error('Failed to fetch user info', e);
      }
    },
    onError: (err) => {
      console.error('Google Login Error', err);
    },
  });

  const logout = () => {
    // Notify backend for logging
    try {
      if (user) {
        fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          credentials: 'include'
        }).catch(() => {});
      }
    } catch {}
    try {
      googleLogout();
    } catch {}
    setUser(null);
    setShowUserMenu(false);
  };

  useEffect(() => {
    if (!showUserMenu) return;
    const onPointerDown = (e) => {
      const el = userMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setShowUserMenu(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (!showLangMenu) return;
    const onPointerDown = (e) => {
      const el = langMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setShowLangMenu(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowLangMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showLangMenu]);

  const currentLng = (() => {
    const p = String(window.location.pathname || '');
    const fromPath = getLngFromPathname(p);
    if (fromPath === 'ja' || fromPath === 'en') return fromPath;
    const il = String(i18n.language || '').toLowerCase();
    return il.startsWith('en') ? 'en' : 'ja';
  })();

  const switchToLng = (lng) => {
    const target = (lng === 'en') ? 'en' : 'ja';
    const path = String(window.location.pathname || '');
    const search = String(window.location.search || '');
    const hash = String(window.location.hash || '');
    let nextPath = path;
    if (/^\/(ja|en)(\/|$)/.test(nextPath)) {
      nextPath = nextPath.replace(/^\/(ja|en)(?=\/|$)/, `/${target}`);
    } else {
      if (nextPath === '/' || nextPath === '') nextPath = `/${target}/`;
      else nextPath = `/${target}${nextPath.startsWith('/') ? nextPath : `/${nextPath}`}`;
    }
    if (nextPath === `/${target}`) nextPath = `/${target}/`;
    window.location.assign(`${nextPath}${search}${hash}`);
  };

  const FlagSvg = ({ lng, className }) => {
    if (lng === 'en') {
      return (
        <svg viewBox="0 0 512 512" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#FFF" d="M0 85.331h512v341.337H0z" />
          <path fill="#B22234" d="M0 85.331h512v26.256H0zM0 137.843h512v26.256H0zM0 190.355h512v26.256H0zM0 242.867h512v26.256H0zM0 295.379h512v26.256H0zM0 347.891h512v26.256H0zM0 400.402h512v26.266H0z" />
          <path fill="#3C3B6E" d="M0 85.331h204.8v183.792H0z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 512 512" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="#FFF" d="M0 85.331h512v341.337H0z" />
        <path fill="#BC002D" d="M256 170.667c-47.11 0-85.333 38.223-85.333 85.333s38.223 85.333 85.333 85.333 85.333-38.223 85.333-85.333-38.223-85.333-85.333-85.333z" />
      </svg>
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromQuery = params.get('token');
    if (tokenFromQuery) {
      setToken(tokenFromQuery);
      // If on /verify path, clean up URL to /
      if (/^\/(ja|en)\/verify\/?$/.test(window.location.pathname) || window.location.pathname === '/verify' || window.location.pathname === '/verify/') {
        window.history.replaceState(null, '', getHomePath());
      }
      return;
    }
    try {
      const s = sessionStorage.getItem('email_verify_token');
      if (s) setToken(s);
    } catch {}
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!token) return;
      try {
        const resp = await fetch(`${API_BASE}/api/auth/email/verify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ token, lng: currentLng })
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
        setVerifiedEmail(data.email || '');
        if (data && typeof data.desired_plan === 'string' && data.desired_plan) {
          setSelectedPlan(String(data.desired_plan).toLowerCase());
        }
        setShowVerifyModal(true);
      } catch (e) {
        setMessage(t('auth.verifyFailed', { error: (e && e.message ? e.message : String(e)) }));
      }
    };

    run();
  }, [token, API_BASE, t]);

  useEffect(() => {
    const loadPlans = async () => {
      if (!showPricing) return;
      try {
        const resp = await fetch(`${API_BASE}/api/auth/plans`, { credentials: 'include' });
        const data = await resp.json();
        setPlans(Array.isArray(data?.plans) ? data.plans : []);
      } catch (e) {
        console.warn('Load plans failed', e);
      }
      try {
        const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (me.ok) {
          const meData = await me.json();
          setUser((prev) => ({
            ...(prev||{}),
            points: meData?.user?.points || (prev ? prev.points : 0),
            plan: meData?.user?.plan || (prev ? prev.plan : 'free'),
            signup_method: meData?.user?.signup_method || (prev ? prev.signup_method : undefined),
          }));
        }
      } catch (e) {
        console.warn('Load me failed', e);
      }
    };
    loadPlans();
  }, [showPricing, API_BASE]);

  useEffect(() => {
    (async () => {
      try {
        const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (me.ok) {
          const meData = await me.json();
          setUser({
            name: meData?.user?.name,
            email: meData?.user?.email,
            picture: meData?.user?.picture,
            points: meData?.user?.points || 0,
            plan: meData?.user?.plan || 'free',
            signup_method: meData?.user?.signup_method,
          });
        }
      } catch (e) {
        console.warn('Init auth check failed', e);
      }
    })();
  }, [API_BASE]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isReturn = params.get('paypal_return');
    const orderId = params.get('token');
    if (isReturn && orderId) {
      try {
        sessionStorage.setItem('paypal_order_id', orderId);
        sessionStorage.setItem('paypal_order_run', '1');
      } catch (e) { console.warn('SS set failed', e); }
      window.location.replace(getHomePath());
      return;
    }
    try {
      const runFlag = sessionStorage.getItem('paypal_order_run');
      const storedId = sessionStorage.getItem('paypal_order_id');
      const processed = sessionStorage.getItem('paypal_order_processed');
      if (runFlag && storedId && !processed) {
        try { sessionStorage.setItem('paypal_order_processed', '1'); } catch (e) { console.warn('SS set failed', e); }
        (async () => {
          try {
            const prevPts = (user && typeof user.points === 'number') ? user.points : 0;
            const resp = await fetch(`${API_BASE}/api/auth/paypal/capture`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ order_id: storedId })
            });
            let data = null;
            try { data = await resp.json(); } catch (e) { await resp.text().catch(()=> ''); }
            const statRaw = (data && (data.status || (data.ok ? 'COMPLETED' : ''))) ? (data.status || 'COMPLETED') : '';
            const stat = String(statRaw || '').trim().toUpperCase();
            setTopupStatus(stat);
            try { console.log('[topup] modal status', stat); } catch {}
            setShowTopupModal(true);
            try {
              const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
              if (me.ok) {
                const meData = await me.json();
                const newPts = meData?.user?.points || 0;
                if (newPts > prevPts) setTopupStatus('COMPLETED');
                setUser((prev) => ({ ...(prev||{}), points: newPts }));
              }
            } catch (e) { console.warn('me fetch failed', e); }
          } catch (e) { console.warn('Capture failed', e); }
          try { sessionStorage.removeItem('paypal_order_run'); sessionStorage.removeItem('paypal_order_id'); sessionStorage.removeItem('paypal_order_processed'); } catch (e) { console.warn('SS remove failed', e); }
        })();
      }
    } catch (e) { console.warn('SS get failed', e); }
  }, [API_BASE, user]);

  useEffect(() => {
    if (!showQueueModal) return;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/auth/queue/list?limit=10`, { credentials: 'include', cache: 'no-store' });
        const data = await resp.json().catch(()=>({}));
        if (cancelled) return { ok: false, items: [] };
        if (resp.ok && data && Array.isArray(data.items)) {
          setQueueItems(data.items);
          return { ok: true, items: data.items };
        }
      } catch {}
      return { ok: false, items: [] };
    };
    const tick = async () => {
      const r = await load();
      if (cancelled) return;
      const items = Array.isArray(r.items) ? r.items : [];
      const hasInProgress = items.some((it) => {
        const downloadable = !!it?.downloadable;
        const expired = !!it?.expired;
        const p = Number(it?.in_progress || 0);
        return !downloadable && !expired && p >= 0 && p < 100;
      });
      if (!r.ok || hasInProgress) {
        timer = setTimeout(tick, 3000);
        return;
      }
      timer = setTimeout(tick, 3000);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [showQueueModal, API_BASE]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSubReturn = params.get('paypal_sub_return');
    const subId = params.get('subscription_id') || params.get('token');
    if (isSubReturn && subId) {
      try {
        sessionStorage.setItem('paypal_sub_id', subId);
        sessionStorage.setItem('paypal_sub_run', '1');
        const pc = sessionStorage.getItem('sub_plan_code') || '';
        if (pc) sessionStorage.setItem('paypal_sub_plan_code', pc);
      } catch {}
      window.location.replace(getHomePath());
      return;
    }
    try {
      const runFlag = sessionStorage.getItem('paypal_sub_run');
      const storedId = sessionStorage.getItem('paypal_sub_id');
      if (runFlag && storedId) {
        (async () => {
          try {
            const resp = await fetch(`${API_BASE}/api/auth/paypal/confirm-subscription`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ subscription_id: storedId })
            });
            let data = null;
            try { data = await resp.json(); } catch (e) { await resp.text().catch(()=> ''); }
            const stat = (data && (data.status || data.next_billing_date || data.ok)) ? (data.status || '') : '';
            setSubStatus(String(stat || ''));
            const planCodeStored = sessionStorage.getItem('paypal_sub_plan_code') || '';
            setSubPlanCode(planCodeStored);
            setShowSubModal(true);
            try {
              const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
              if (me.ok) {
                const meData = await me.json();
                setUser((prev) => ({
                  ...(prev||{}),
                  points: meData?.user?.points || 0,
                  plan: meData?.user?.plan || (prev ? prev.plan : 'free'),
                }));
              }
            } catch {}
          } catch {}
          try { sessionStorage.removeItem('paypal_sub_run'); sessionStorage.removeItem('paypal_sub_id'); sessionStorage.removeItem('paypal_sub_plan_code'); } catch {}
        })();
      }
    } catch {}
  }, [API_BASE]);

  useEffect(() => {
    if (!showHistoryModal) return;
    setHistoryBusy(true);
    setHistoryError('');
    setHistoryItems([]);
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/auth/payments/history?limit=100`, { credentials: 'include', cache: 'no-store' });
        let data = null;
        try { data = await resp.json(); } catch (e) { await resp.text().catch(()=> ''); }
        if (!resp.ok) {
          setHistoryError(t('history.fetchFailed'));
          setHistoryBusy(false);
          return;
        }
        const items = (data && Array.isArray(data.items)) ? data.items : [];
        setHistoryItems(items);
        setHistoryBusy(false);
      } catch (e) {
        setHistoryError(t('history.fetchFailed'));
        setHistoryBusy(false);
      }
    })();
  }, [showHistoryModal, API_BASE, t]);

  const requestEmailSignup = async () => {
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/email/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email, plan: selectedPlan, lng: currentLng })
      });
      let data = null; let text = '';
      try { data = await resp.json(); } catch (e) { text = await resp.text().catch(()=> ''); }
      if (!resp.ok) throw new Error((data && data.error) ? data.error : (text || `HTTP ${resp.status}`));
      setShowSignup(false);
      setShowEmailLogin(false);
      setShowLoginModal(false);
      setShowSentPage(true);
    } catch (e) {
      setMessage(t('auth.sendFailed', { error: String(e) }));
    }
  };

  const checkUsername = async () => {
    setMessage('');
    setUsernamePolicyError('');
    if (!isValidUsername(username)) {
      setUsernamePolicyError(USERNAME_POLICY_TEXT);
      return;
    }
    const resp = await fetch(`${API_BASE}/api/auth/email/check-username?username=${encodeURIComponent(username)}`);
    let data = null; let text = '';
    try { data = await resp.json(); } catch (e) { text = await resp.text().catch(() => ''); }
    if (!resp.ok) {
      const msg = (data && data.error) ? data.error : (text || `HTTP ${resp.status}`);
      throw new Error(msg);
    }
    if (data.available) setMessage(USERNAME_AVAILABLE_MESSAGE); else setMessage(USERNAME_TAKEN_MESSAGE);
  };

  const completeEmailSignup = async () => {
    setPasswordMismatch(false);
    setPasswordPolicyError('');
    setUsernamePolicyError('');
    if (!isValidUsername(username)) {
      setUsernamePolicyError(USERNAME_POLICY_TEXT);
      return;
    }
    if (!isStrongPassword(password)) {
      setPasswordPolicyError(PASSWORD_POLICY_TEXT);
      return;
    }
    if (password !== password2) {
      setPasswordMismatch(true);
      return;
    }
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/email/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ token, username, password, lng: currentLng })
      });
      let data = null;
      let text = '';
      try {
        data = await resp.json();
      } catch (e) {
        text = await resp.text().catch(() => '');
      }
      if (!resp.ok) {
        const msg = (data && data.error) ? data.error : (text || `HTTP ${resp.status}`);
        throw new Error(msg);
      }
      
      // Do not log in automatically
      // We just show the completion modal

      setMessage('');
      setShowVerifyModal(false);
      try { sessionStorage.removeItem('email_verify_token'); } catch {}
      window.history.replaceState(null, '', getHomePath());
      setShowCompleteModal(true);
    } catch (e) {
      setMessage(t('auth.signupFailed', { error: (e && e.message ? e.message : String(e)) }));
    }
  };

  const changePlan = async (planCode) => {
    if (!user || !planCode) {
      setShowPricing(false);
      return;
    }
    const planCodeLower = String(planCode || '').toLowerCase();
    if (planCodeLower === 'free') {
      try {
        const resp = await fetch(`${API_BASE}/api/auth/paypal/cancel-subscription`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({})
        });
        let data = null;
        let text = '';
        try { data = await resp.json(); } catch (e) { text = await resp.text().catch(() => ''); }
        if (!resp.ok) {
          const msg = (data && data.error) ? data.error : (text || `HTTP ${resp.status}`);
          throw new Error(msg);
        }
        try {
          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
          if (me.ok) {
            const meData = await me.json();
            setUser((prev) => ({
              ...(prev || {}),
              points: meData?.user?.points || (prev ? prev.points : 0),
              plan: meData?.user?.plan || (prev ? prev.plan : 'free'),
            }));
          }
        } catch {}
        setPlanChangeBusy('');
        setShowPricing(false);
        setShowDowngradeModal(true);
      } catch (e) {
        setPlanChangeBusy('');
      }
      return;
    }

    setShowPaypalRedirectModal(true);
    try {
      try { sessionStorage.setItem('sub_plan_code', planCode); } catch {}
      const resp2 = await fetch(`${API_BASE}/api/auth/paypal/create-subscription`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ plan: planCode, email: user.email })
      });
      let d2 = null;
      let t2 = '';
      try { d2 = await resp2.json(); } catch (e) { t2 = await resp2.text().catch(() => ''); }
      if (!resp2.ok) {
        const msg = (d2 && d2.error) ? d2.error : (t2 || `HTTP ${resp2.status}`);
        throw new Error(msg);
      }
      const url = d2?.approve_url;
      if (!url) throw new Error('approve_url missing');
      window.location.href = url;
    } catch (e) {
      setShowPaypalRedirectModal(false);
      setPlanChangeBusy('');
      setShowPricing(true);
    }
  };

  const changePassword = async () => {
    setCpMessage('');
    setCpPasswordMismatch(false);
    setCpPasswordPolicyError('');
    if (!cpCurrentPassword || !cpNewPassword || !cpNewPassword2) {
      setCpMessage(t('auth.missingFields'));
      return;
    }
    if (!isStrongPassword(cpNewPassword)) {
      setCpPasswordPolicyError(PASSWORD_POLICY_TEXT);
      return;
    }
    if (cpNewPassword !== cpNewPassword2) {
      setCpPasswordMismatch(true);
      return;
    }
    setCpBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/password/change`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ current_password: cpCurrentPassword, new_password: cpNewPassword })
      });
      let data = null; let text = '';
      try { data = await resp.json(); } catch (e) { text = await resp.text().catch(() => ''); }
      if (!resp.ok) {
        const msg = (data && data.error) ? data.error : (text || `HTTP ${resp.status}`);
        setCpMessage(String(msg));
        setCpBusy(false);
        return;
      }
      setCpBusy(false);
      setShowChangePasswordModal(false);
      setCpCurrentPassword('');
      setCpNewPassword('');
      setCpNewPassword2('');
    } catch (e) {
      setCpMessage(t('auth.changePasswordFailed'));
      setCpBusy(false);
    }
  };

  const createTopup = async () => {
    if (!user) return;
    setShowPaypalRedirectModal(true);
    try {
      const units = Math.max(1, Number(topupUnits || 1));
      const resp = await fetch(`${API_BASE}/api/auth/paypal/create-topup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ units })
      });
      const data = await resp.json();
      const url = data?.approve_url;
      if (url) {
        window.location.href = url;
      } else {
        setShowPaypalRedirectModal(false);
        setTopupBusy(false);
      }
    } catch {
      setShowPaypalRedirectModal(false);
      setTopupBusy(false);
    }
  };

  const emailLogin = async () => {
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      let data = null;
      let text = '';
      try {
        data = await resp.json();
      } catch (e) {
        text = await resp.text().catch(() => '');
      }
      if (!resp.ok) {
        const msg = (data && data.error) ? data.error : (text || `HTTP ${resp.status}`);
        throw new Error(msg);
      }
      setUser({
        name: data?.user?.name,
        email: data?.user?.email,
        picture: data?.user?.picture,
        points: data?.user?.points || 0,
        plan: data?.user?.plan,
        signup_method: data?.user?.signup_method,
      });
      setShowEmailLogin(false);
      setShowLoginModal(false);
    } catch (e) {
      setMessage(t('auth.loginFailed', { error: (e && e.message ? e.message : String(e)) }));
    }
  };

  const DEFAULT_AVATAR = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="%23f1f5f9"/><circle cx="32" cy="24" r="12" fill="%239ca3af"/><path d="M8 56c4-10 14-16 24-16s20 6 24 16" fill="%239ca3af"/></svg>';
  const BRAND_LOGO = `${process.env.PUBLIC_URL}/logo_256.png`;
  const HERO_CONV_3D = `${process.env.PUBLIC_URL}/pics/2dconv3d.jpg`;
  const HERO_SEP_MUSIC = `${process.env.PUBLIC_URL}/pics/sepmusic.jpg`;
  const HERO_NOISE_REMOVER = `${process.env.PUBLIC_URL}/pics/noiseremover.jpg`;
  const HERO_FACESWAP = `${process.env.PUBLIC_URL}/pics/faceswap.jpg`;
  const HERO_PIC2DANCE = `${process.env.PUBLIC_URL}/movies/pic2dance.mp4`;
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header with Menu Bar */}
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-[rgba(15,23,42,0.65)] backdrop-blur-md backdrop-saturate-150 border-b border-white/10">
        <div className="w-full pr-4 pl-0 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <a href={getHomePath()} className="ml-2.5 flex items-center space-x-3 text-white hover:text-blue-300 transition-colors duration-200">
              <img src={BRAND_LOGO} alt="MAKAREUS" className="w-10 h-10 md:w-12 md:h-12" />
              <div className="flex items-baseline space-x-2">
                <span className="text-xl md:text-2xl font-bold leading-none">MAKAREUS</span>
                <span className="text-base font-medium leading-none">The Best of AI Tools</span>
              </div>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <a
                href="#queue"
                className="flex items-center space-x-2 text-white hover:text-blue-300 transition-colors duration-200 group"
                onClick={(e)=>{ e.preventDefault(); setShowQueueModal(true); }}
              >
                <Activity className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium">{t('queue.status')}</span>
              </a>
            )}
            <a
              href="#pricing"
              onClick={(e) => { e.preventDefault(); setShowPricing(true); }}
              className="flex items-center space-x-2 text-white hover:text-blue-300 transition-colors duration-200 group"
            >
              <CreditCard className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
              <span className="font-medium">{t('pricing.nav')}</span>
            </a>
            {
              user ? (
                <div className="flex items-center space-x-3">
                  <div className="relative" ref={userMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowUserMenu(v => !v)}
                      className="flex items-center space-x-2"
                    >
                      <img
                        src={user.picture || DEFAULT_AVATAR}
                        alt={user.name || 'account'}
                        className="w-8 h-8 rounded-full ring-2 ring-white/30 shadow-md"
                      />
                      <ChevronDown className="w-4 h-4 text-white/80" />
                    </button>
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-44 rounded-md bg-white text-gray-900 shadow-lg ring-1 ring-black/10 z-[9999] overflow-hidden">
                        {user?.signup_method !== 'google' && (
                          <button
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                            onClick={() => { setShowUserMenu(false); setCpMessage(''); setCpPasswordMismatch(false); setCpPasswordPolicyError(''); setCpCurrentPassword(''); setCpNewPassword(''); setCpNewPassword2(''); setShowChangePasswordModal(true); }}
                          >
                            <Key className="w-4 h-4" />
                            <span>{t('auth.changePassword')}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                          onClick={() => { setShowUserMenu(false); setShowHistoryModal(true); }}
                        >
                          <Receipt className="w-4 h-4" />
                          <span>{t('history.title')}</span>
                        </button>
                        <button
                          type="button"
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                          onClick={() => { setShowUserMenu(false); logout(); }}
                        >
                          <LogOut className="w-4 h-4" />
                          <span>{t('auth.logout')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-white/80 text-sm">{t('user.pointsLabel', { points: user.points ?? 0 })}</div>
                  <div className="relative" ref={langMenuRef}>
                    <button
                      type="button"
                      className="flex items-center"
                      onClick={() => setShowLangMenu(v => !v)}
                    >
                      <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-white/20 shadow-sm">
                        <FlagSvg lng={currentLng} className="w-full h-full" />
                      </div>
                    </button>
                    {showLangMenu && (
                      <div className="absolute right-0 mt-2 w-44 rounded-md bg-white text-gray-900 shadow-lg ring-1 ring-black/10 z-[9999] overflow-hidden">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                          onClick={() => { setShowLangMenu(false); switchToLng('ja'); }}
                        >
                          <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-black/10">
                            <FlagSvg lng="ja" className="w-full h-full" />
                          </div>
                          <span>{t('lang.ja')}</span>
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                          onClick={() => { setShowLangMenu(false); switchToLng('en'); }}
                        >
                          <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-black/10">
                            <FlagSvg lng="en" className="w-full h-full" />
                          </div>
                          <span>{t('lang.en')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="relative" ref={langMenuRef}>
                    <button
                      type="button"
                      className="flex items-center"
                      onClick={() => setShowLangMenu(v => !v)}
                    >
                      <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-white/20 shadow-sm">
                        <FlagSvg lng={currentLng} className="w-full h-full" />
                      </div>
                    </button>
                    {showLangMenu && (
                      <div className="absolute right-0 mt-2 w-44 rounded-md bg-white text-gray-900 shadow-lg ring-1 ring-black/10 z-[9999] overflow-hidden">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                          onClick={() => { setShowLangMenu(false); switchToLng('ja'); }}
                        >
                          <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-black/10">
                            <FlagSvg lng="ja" className="w-full h-full" />
                          </div>
                          <span>{t('lang.ja')}</span>
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                          onClick={() => { setShowLangMenu(false); switchToLng('en'); }}
                        >
                          <div className="w-7 h-5 rounded overflow-hidden ring-1 ring-black/10">
                            <FlagSvg lng="en" className="w-full h-full" />
                          </div>
                          <span>{t('lang.en')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25 group"
                  >
                    <LogIn className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    <span>{t('auth.login')}</span>
                  </button>
                </div>
              )
            }
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center py-12 px-4 mt-16 md:mt-20">
        <div className="w-full max-w-6xl" id="features">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-widest uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full">
              {t('landing.coreTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-white">{t('landing.headline')}</h2>
            <div className="w-20 h-1.5 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto"></div>
            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
              {t('landing.subheadline')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl overflow-hidden bg-white/10 border border-white/10 shadow-xl">
              <div className="relative">
                <img src={HERO_CONV_3D} alt={t('features.conv3d.alt')} className="w-full h-56 object-cover" />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-black/0">
                  <div className="text-white text-lg font-semibold text-outline-title">{t('features.conv3d.title')}</div>
                  <div className="text-slate-200 text-xs md:text-sm mt-1 text-outline-body">
                    {t('features.conv3d.desc')}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => openFeature('conv')}
                  className="w-full px-4 py-3 rounded-xl bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 font-medium shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
                >
                  {t('common.useThisFeature')}
                </button>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-white/10 border border-white/10 shadow-xl">
              <div className="relative">
                <video src={HERO_PIC2DANCE} className="w-full h-56 object-cover" autoPlay loop muted playsInline />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-black/0">
                  <div className="text-white text-lg font-semibold text-outline-title">{t('features.pic2dance.title')}</div>
                  <div className="text-slate-200 text-xs md:text-sm mt-1 text-outline-body">
                    {t('features.pic2dance.desc')}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => openFeature('pic2dance')}
                  className="w-full px-4 py-3 rounded-xl bg-pink-600/20 hover:bg-pink-600 text-pink-300 hover:text-white border border-pink-500/30 font-medium shadow-lg hover:shadow-pink-500/25 transition-all duration-200"
                >
                  {t('common.useThisFeature')}
                </button>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-white/10 border border-white/10 shadow-xl">
              <div className="relative">
                <img src={HERO_SEP_MUSIC} alt={t('features.sep.alt')} className="w-full h-56 object-cover" />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-black/0">
                  <div className="text-white text-lg font-semibold text-outline-title">{t('features.sep.title')}</div>
                  <div className="text-slate-200 text-xs md:text-sm mt-1 text-outline-body">
                    {t('features.sep.desc')}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => openFeature('sep')}
                  className="w-full px-4 py-3 rounded-xl bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white border border-purple-500/30 font-medium shadow-lg hover:shadow-purple-500/25 transition-all duration-200"
                >
                  {t('common.useThisFeature')}
                </button>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-white/10 border border-white/10 shadow-xl">
              <div className="relative">
                <img src={HERO_NOISE_REMOVER} alt={t('features.denoise.alt')} className="w-full h-56 object-cover" />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-black/0">
                  <div className="text-white text-lg font-semibold text-outline-title">{t('features.denoise.title')}</div>
                  <div className="text-slate-200 text-xs md:text-sm mt-1 text-outline-body">
                    {t('features.denoise.desc')}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => openFeature('denoise')}
                  className="w-full px-4 py-3 rounded-xl bg-emerald-600/20 hover:bg-emerald-600 text-emerald-400 hover:text-white border border-emerald-500/30 font-medium shadow-lg hover:shadow-emerald-500/25 transition-all duration-200"
                >
                  {t('common.useThisFeature')}
                </button>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-white/10 border border-white/10 shadow-xl">
              <div className="relative">
                <img src={HERO_FACESWAP} alt={t('features.faceswap.alt')} className="w-full h-56 object-cover" />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-black/0">
                  <div className="text-white text-lg font-semibold text-outline-title">{t('features.faceswap.title')}</div>
                  <div className="text-slate-200 text-xs md:text-sm mt-1 text-outline-body">
                    {t('features.faceswap.desc')}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  type="button"
                  onClick={() => openFeature('faceswap')}
                  className="w-full px-4 py-3 rounded-xl bg-indigo-600/20 hover:bg-indigo-600 text-indigo-400 hover:text-white border border-indigo-500/30 font-medium shadow-lg hover:shadow-indigo-500/25 transition-all duration-200"
                >
                  {t('common.useThisFeature')}
                </button>
              </div>
            </div>


          </div>
          {/* FAQ Section */}
          <section id="faq" className="py-16 px-4 bg-slate-950 border-b border-white/5 mt-12 rounded-2xl">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-4 text-white">{t('faq.title')}</h2>
                <p className="text-slate-400">{t('faq.subtitle')}</p>
              </div>
              <div className="space-y-4">
                <details className="rounded-2xl p-4 bg-white/5 border border-white/10 group">
                  <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-white">
                    <span>{t('faq.q.pointsExpiry')}</span>
                    <span className="text-slate-500 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 text-slate-300 text-sm leading-relaxed">
                    {t('faq.a.pointsExpiry')}
                  </div>
                </details>
                <details className="rounded-2xl p-4 bg-white/5 border border-white/10 group">
                  <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-white">
                    <span>{t('faq.q.formats')}</span>
                    <span className="text-slate-500 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 text-slate-300 text-sm leading-relaxed">
                    {t('faq.a.formats')}
                  </div>
                </details>
                <details className="rounded-2xl p-4 bg-white/5 border border-white/10 group">
                  <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-white">
                    <span>{t('faq.q.commercial')}</span>
                    <span className="text-slate-500 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 text-slate-300 text-sm leading-relaxed">
                    {t('faq.a.commercial1')}<br /><br />
                    {t('faq.a.commercial2')}
                  </div>
                </details>
                <details className="rounded-2xl p-4 bg-white/5 border border-white/10 group">
                  <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-white">
                    <span>{t('faq.q.payment')}</span>
                    <span className="text-slate-500 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 text-slate-300 text-sm leading-relaxed">
                    {t('faq.a.payment')}
                  </div>
                </details>
                <details className="rounded-2xl p-4 bg-white/5 border border-white/10 group">
                  <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-white">
                    <span>{t('faq.q.refund')}</span>
                    <span className="text-slate-500 transition-transform">▼</span>
                  </summary>
                  <div className="mt-4 text-slate-300 text-sm leading-relaxed">
                    {t('faq.a.refund1')}<br /><br />
                    {t('faq.a.refund2')}
                  </div>
                </details>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Subtle Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-1 h-1 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('auth.selectLoginMethod')}</div>
            <div className="space-y-3">
              <button onClick={() => { login(); }} className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded">{t('auth.loginWithGoogle')}</button>
              <button onClick={() => { setShowEmailLogin(true); }} className="w-full bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded">{t('auth.loginWithEmail')}</button>
            </div>
            <div className="text-center mt-4">
              <button className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setShowLoginModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('auth.changePassword')}</div>
            <div className="space-y-3">
              <input type="password" value={cpCurrentPassword} onChange={e=>setCpCurrentPassword(e.target.value)} placeholder={t('auth.currentPassword')} className="w-full px-3 py-2 rounded border" />
              <input type="password" value={cpNewPassword} onChange={e=>{ setCpNewPassword(e.target.value); setCpPasswordMismatch(false); setCpPasswordPolicyError(''); }} placeholder={t('auth.newPassword')} className="w-full px-3 py-2 rounded border" />
              <input type="password" value={cpNewPassword2} onChange={e=>{ setCpNewPassword2(e.target.value); setCpPasswordMismatch(false); }} placeholder={t('auth.confirmPassword')} className="w-full px-3 py-2 rounded border" />
              {cpPasswordMismatch && <div className="text-red-600 text-sm">{t('auth.passwordMismatch')}</div>}
              <div className={`text-sm ${(cpPasswordPolicyError) ? 'text-red-600' : 'text-gray-700'}`}>{SIGNUP_POLICY_TEXT}</div>
              {cpMessage && <div className="text-sm text-red-600">{cpMessage}</div>}
              <div className="flex justify-between items-center mt-2">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowChangePasswordModal(false)}>{t('common.close')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={cpBusy} onClick={changePassword}>{t('common.change')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[720px] text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('history.title')}</div>
            <div className="space-y-2 max-h-[420px] overflow-auto">
              {historyBusy ? (
                <div className="text-sm text-gray-700">{t('common.loading')}</div>
              ) : historyError ? (
                <div className="text-sm text-red-600">{historyError}</div>
              ) : (historyItems && historyItems.length > 0) ? (
                historyItems.slice(0, 100).map((it, idx) => {
                  const when = it.created_at || it.time || it.date || '';
                  const type = it.type || it.kind || '';
                  const amt = (typeof it.amount_usd !== 'undefined') ? t('history.usdAmount', { amount: it.amount_usd }) : (it.amount || '');
                  const status = (it.status || '').toString().toUpperCase();
                  const detail = it.description || it.note || it.id || '';
                  return (
                    <div key={it.id || idx} className="flex justify-between text-sm border rounded px-3 py-2">
                      <div className="truncate mr-4">
                        <span className="text-gray-600 mr-2">{when}</span>
                        <span className="mr-2">{type}</span>
                        {amt && <span className="mr-2">{amt}</span>}
                        {detail && <span className="text-gray-500">{detail}</span>}
                      </div>
                      <div className="w-32 text-right">{status || ''}</div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-gray-700">{t('common.noItems')}</div>
              )}
            </div>
            <div className="mt-4 text-xs text-gray-700">{t('history.limitNote')}</div>
            <div className="text-center mt-4">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowHistoryModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showTopupModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('pricing.topupTitle')}</div>
            {(topupStatus === 'COMPLETED' || topupStatus === 'CAPTURED') ? (
              <>
                <div>{t('pricing.topupSuccess')}</div>
                <div className="mt-6">
                  <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowTopupModal(false)}>{t('common.close')}</button>
                </div>
              </>
            ) : (
              <>
                <div>{t('pricing.topupFailed')}</div>
                <div className="mt-6">
                  <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowTopupModal(false)}>{t('common.close')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showSupportModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] px-4">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-auto bg-slate-900 text-white rounded-3xl border border-white/10 shadow-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600/20 rounded-2xl text-blue-400 mb-4">
                <span className="text-2xl">✉</span>
              </div>
              <h1 className="text-3xl font-bold tracking-tight">{t('support.title')}</h1>
              <p className="text-slate-400 mt-2 text-sm">{t('support.subtitle')}</p>
            </div>

            <form className="space-y-6" onSubmit={async (e) => {
              e.preventDefault();
              if (!supportCanSubmit) return;
              setSupportSendBusy(true);
              setSupportSendError('');
              const fallbackEnv = parseUserAgent();
              const os = String(supportEnvOs || fallbackEnv.os || '');
              const browser = String(supportEnvBrowser || fallbackEnv.browser || '');
              const fd = new FormData();
              fd.append('category', String(supportCategory || ''));
              fd.append('os', os);
              fd.append('browser', browser);
              fd.append('query', String(supportQuery || ''));
              if (supportAttachFiles && supportAttachFiles[0]) fd.append('attach1', supportAttachFiles[0]);
              if (supportAttachFiles && supportAttachFiles[1]) fd.append('attach2', supportAttachFiles[1]);
              try {
                const resp = await fetch(`${API_BASE}/api/auth/support`, { method: 'POST', body: fd, credentials: 'include' });
                const data = await resp.json().catch(() => ({}));
                if (resp.ok && data && data.ok) {
                  setShowSupportModal(false);
                  setSupportSentUuid(String(data.uuid || ''));
                  setShowSupportSentModal(true);
                  resetSupportState();
                } else {
                  setSupportSendError(String((data && data.error) ? data.error : t('support.sendFailed')));
                }
              } catch {
                setSupportSendError(t('support.sendFailed'));
              }
              setSupportSendBusy(false);
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.email')}</label>
                  <input type="email" value={String(user?.email || '')} disabled className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.category')} <span className="text-rose-500">*</span></label>
                  <select value={supportCategory} onChange={(e) => setSupportCategory(e.target.value)} className="w-full px-4 py-3 rounded-xl appearance-none cursor-pointer bg-slate-950/70 border border-white/10 text-white">
                    <option value="">{t('support.placeholders.select')}</option>
                    <option value="bug">{t('support.categoryOptions.bug')}</option>
                    <option value="feature">{t('support.categoryOptions.feature')}</option>
                    <option value="pricing">{t('support.categoryOptions.pricing')}</option>
                    <option value="other">{t('support.categoryOptions.other')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.os')}</label>
                  <input type="text" value={supportEnvOs || t('support.unknown')} disabled className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.browser')}</label>
                  <input type="text" value={supportEnvBrowser || t('support.unknown')} disabled className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.details')} <span className="text-rose-500">*</span></label>
                <textarea rows="5" value={supportQuery} onChange={(e) => setSupportQuery(e.target.value)} placeholder={t('support.placeholders.details')} className="w-full px-4 py-3 rounded-xl resize-none bg-slate-950/70 border border-white/10 text-white" />
                <div className="text-[10px] text-slate-500 mt-2">{t('support.hints.minChars')}</div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('support.fields.attachments')}</label>
                <div className="rounded-2xl p-6 text-center cursor-pointer border-2 border-dashed border-white/15 bg-white/5 hover:border-blue-500/60 transition" onClick={() => { if (supportFileInputRef.current) supportFileInputRef.current.click(); }}>
                  <input ref={supportFileInputRef} type="file" className="hidden" multiple onChange={(e) => {
                    const files = Array.from((e.target.files || [])).slice(0, 2);
                    setSupportAttachError('');
                    if (files.length === 0) { setSupportAttachFiles([]); return; }
                    const oversize = files.find(f => Number(f.size || 0) > MAX_UPLOAD_BYTES);
                    if (oversize) {
                      setSupportAttachFiles([]);
                      if (supportFileInputRef.current) supportFileInputRef.current.value = '';
                      setSupportAttachError(MAX_UPLOAD_ERROR);
                      return;
                    }
                    setSupportAttachFiles(files);
                  }} />
                  <div className="text-slate-300 font-medium text-sm">{t('support.attach.clickToUpload')}</div>
                  <div className="text-[10px] text-slate-500 mt-2">{t('support.attach.maxSize')}</div>
                  {supportAttachFiles && supportAttachFiles.length > 0 && (
                    <div className="mt-3 text-xs text-blue-300 font-bold">
                      {supportAttachFiles.map(f => String(f.name || '')).join(' / ')}
                    </div>
                  )}
                </div>
                {supportAttachError && <div className="mt-2 text-sm text-red-400 font-bold">{supportAttachError}</div>}
              </div>

              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="flex items-start gap-3">
                  <div className="flex items-center h-5">
                    <input id="support_privacy" type="checkbox" checked={supportPrivacyOk} onChange={(e) => setSupportPrivacyOk(!!e.target.checked)} className="w-5 h-5 cursor-pointer accent-blue-500" />
                  </div>
                  <label htmlFor="support_privacy" className="text-sm text-slate-300 leading-tight cursor-pointer">
                    <button type="button" className="text-blue-400 hover:underline" onClick={() => setShowPrivacyModal(true)}>{t('legal.privacy.title')}</button>{t('support.agreeSuffix')} <span className="text-rose-500">*</span>
                  </label>
                </div>
              </div>

              {supportSendError && <div className="text-sm text-red-400 font-bold">{supportSendError}</div>}

              <div className="pt-2 flex items-center justify-between">
                <button type="button" className="px-4 py-2 rounded bg-slate-800 text-white" onClick={() => { setShowSupportModal(false); resetSupportState(); }}>{t('common.close')}</button>
                <button type="submit" disabled={!supportCanSubmit} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                  {supportSendBusy ? t('support.sending') : t('support.sendMessage')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSupportSentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2100]">
          <div className="w-full max-w-lg text-center">
            <div className="bg-slate-900/70 backdrop-blur-xl rounded-[2.5rem] p-10 md:p-16 border border-white/10 shadow-2xl">
              <div className="relative inline-flex mb-8">
                <div className="w-24 h-24 bg-gradient-to-tr from-emerald-500 to-teal-400 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <span className="text-4xl text-white">✓</span>
                </div>
                <div className="absolute -inset-4 border border-emerald-500/20 rounded-full animate-ping"></div>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">{t('support.sent.title')}</h1>
              <p className="text-slate-400 leading-relaxed mb-10">
                {t('support.sent.body')}
                <span className="text-xs mt-2 block opacity-60">{t('support.sent.note')}</span>
              </p>

              <div className="space-y-3">
                <button className="block w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98]" onClick={() => { setShowSupportSentModal(false); setSupportSentUuid(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  {t('common.backHome')}
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-white/5">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{t('support.sent.referenceNumber')}</p>
                <p className="text-sm font-mono text-slate-300">{supportSentUuid || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSepModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('processing.sep.title')}</div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (sepFileInputRef.current) sepFileInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{sepFile ? String(sepFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input ref={sepFileInputRef} type="file" className="hidden" accept="audio/*,video/*,.mp3,.wav,.mp4,.m4a,.aac,.ogg,.flac,.webm" onChange={async (e)=>{
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setSepError(''); setSepFile(null); setSepDuration(0); setSepPoints(0); setSepUploadProgress(0);
                if (!f) return;
                if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setSepError(MAX_UPLOAD_ERROR); return; }
                const name = (f.name||'').toLowerCase();
                const allowed = ['.mp3','.wav','.mp4','.m4a','.aac','.ogg','.flac','.webm'];
                const ok = allowed.some(ext=>name.endsWith(ext)) || (String(f.type||'').startsWith('audio') || String(f.type||'').startsWith('video'));
                if (!ok) { setSepError(t('common.unsupportedFile')); if (sepFileInputRef.current) sepFileInputRef.current.value = ''; return; }
                try {
                  const url = URL.createObjectURL(f);
                  const isAudio = String(f.type||'').startsWith('audio') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.aac') || name.endsWith('.flac') || name.endsWith('.ogg');
                  const el = isAudio ? new Audio() : document.createElement('video');
                  el.preload = 'metadata';
                  el.src = url;
                  await new Promise((resolve, reject)=>{
                    el.onloadedmetadata = ()=> resolve(null);
                    el.onerror = ()=> reject(new Error('metadata error'));
                  });
                  const dur = Math.max(0, Math.round(el.duration || 0));
                  URL.revokeObjectURL(url);
                  if (!dur) { setSepError(t('common.unsupportedFile')); if (sepFileInputRef.current) sepFileInputRef.current.value = ''; return; }
                  setSepFile(f);
                  setSepDuration(dur);
                  setSepPoints(dur);
                } catch {
                  setSepError(t('common.unsupportedFile'));
                  if (sepFileInputRef.current) sepFileInputRef.current.value = '';
                }
                }} />
              </div>
              <div className="space-y-1">
                <label className="flex items-center space-x-2">
                  <input type="radio" name="sepModel" checked={sepModel==='normal'} onChange={()=>setSepModel('normal')} />
                  <span>{t('processing.sep.model4')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="sepModel" checked={sepModel==='6s'} onChange={()=>setSepModel('6s')} />
                  <span>{t('processing.sep.model6')}</span>
                </label>
              </div>
              <div className="text-sm text-gray-700">{t('processing.pointsRate10')}</div>
              {sepDuration > 0 && (
                <div className="text-sm">{t('processing.lengthAndPoints', { seconds: sepDuration, points: sepPoints })}</div>
              )}
              {sepBusy && (
                <div className="mt-2">
                  <div className="text-xs text-gray-700 mb-1">{t('processing.uploadProgress', { progress: sepUploadProgress })}</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${sepUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
              {sepError && <div className="text-sm text-red-600">{sepError}</div>}
              {sepInsufficient && <div className="text-sm text-red-600 text-right">{t('pricing.insufficientPoints')}</div>}
              <div className="mt-4 flex justify-between items-center">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=>{ setShowSepModal(false); resetSepState(); }}>{t('common.close')}</button>
                <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowSepModal(false); resetSepState(); setShowPricing(true); }}>{t('pricing.buyPoints')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!sepFile || !!sepError || sepDuration<=0 || sepBusy || sepInsufficient} onClick={async ()=>{
                  if (!sepFile || sepError || sepDuration<=0 || sepBusy) return;
                  setSepBusy(true);
                  try {
                    const params = new URLSearchParams({
                      job_name: 'separate_audio',
                      file_name: sepFile.name,
                      file_size: String(sepFile.size),
                      file_timelength: String(sepDuration),
                      point_used: String(sepPoints),
                      file_in_options: JSON.stringify(sepModel==='6s' ? {model:'6s'} : {model:'4s'})
                    });
                    setSepUploadProgress(0);
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE}/api/auth/queue/upload?${params.toString()}`, true);
                    xhr.responseType = 'json';
                    xhr.withCredentials = true;
                    xhr.setRequestHeader('Content-Type','application/octet-stream');
                    xhr.upload.onprogress = (e)=>{
                      if (e.lengthComputable) setSepUploadProgress(Math.min(100, Math.round(e.loaded * 100 / e.total)));
                    };
                    const done = await new Promise((resolve)=>{
                      xhr.onload = ()=> resolve({ ok: xhr.status>=200 && xhr.status<300, data: xhr.response });
                      xhr.onerror = ()=> resolve({ ok: false, data: { error: 'network error' } });
                      xhr.send(sepFile);
                    });
                    const respOk = done.ok; const data = done.data || {};
                    if (respOk && data && data.ok) {
                      setShowSepModal(false);
                      setShowSepSuccess(true);
                      setSepBusy(false);
                      setSepUploadProgress(0);
                      setSepFile(null);
                      setSepDuration(0);
                      setSepPoints(0);
                      setSepError('');
                      if (typeof data.points === 'number') {
                        setUser(prev => ({ ...(prev||{}), points: Number(data.points||0) }));
                      } else {
                        try {
                          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
                          if (me.ok) {
                            const meData = await me.json();
                            setUser(prev => ({ ...(prev||{}), points: meData?.user?.points || (prev ? prev.points : 0) }));
                          }
                        } catch {}
                      }
                    } else {
                      setSepError(String((data && data.error) ? data.error : t('errors.serverRegistrationFailed')));
                      setSepBusy(false);
                    }
                  } catch {
                    setSepError(t('errors.serverRegistrationFailed'));
                    setSepBusy(false);
                  }
                }}>{t('processing.start')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPic2danceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[620px] text-gray-900">
            <div className="text-lg font-semibold mb-2">{t('processing.pic2dance.title')}</div>
            <div className="text-sm text-gray-700 mb-3">{t('processing.pic2dance.subtitle')}</div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700">{t('processing.pic2dance.imageLabel')}</div>
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (pic2danceImageInputRef.current) pic2danceImageInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{pic2danceImageFile ? String(pic2danceImageFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input
                  ref={pic2danceImageInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setPic2danceError('');
                    setPic2danceUploadProgress(0);
                    setPic2danceJobSerial(0);
                    setPic2danceImageFile(null);
                    if (!f) return;
                    if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setPic2danceError(MAX_UPLOAD_ERROR); return; }
                    const name = (f.name || '').toLowerCase();
                    const allowed = ['.jpg', '.jpeg', '.png', '.webp'];
                    const ok = allowed.some(ext => name.endsWith(ext)) || String(f.type || '').startsWith('image/');
                    if (!ok) { setPic2danceError(t('common.unsupportedFile')); if (pic2danceImageInputRef.current) pic2danceImageInputRef.current.value = ''; return; }
                    setPic2danceImageFile(f);
                  }}
                />
              </div>

              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700">{t('processing.pic2dance.videoLabel')}</div>
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{pic2danceVideoFile ? String(pic2danceVideoFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input
                  ref={pic2danceVideoInputRef}
                  type="file"
                  className="hidden"
                  accept="video/*,.mp4,.mov,.mkv,.avi,.webm,.m4v,.ts,video/mp2t,video/MP2T"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setPic2danceError('');
                    setPic2danceVideoFile(null);
                    setPic2danceDuration(0);
                    setPic2dancePoints(0);
                    setPic2danceUploadProgress(0);
                    setPic2danceJobSerial(0);
                    if (!f) return;
                    if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setPic2danceError(MAX_UPLOAD_ERROR); return; }
                    const name = (f.name || '').toLowerCase();
                    const allowed = ['.mp4', '.mov', '.mkv', '.avi', '.webm', '.m4v', '.ts'];
                    const ok = allowed.some(ext => name.endsWith(ext)) || String(f.type || '').startsWith('video/');
                    if (!ok) { setPic2danceError(t('common.unsupportedFile')); if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.value = ''; return; }

                    const seq = (pic2danceAnalyzeSeqRef.current || 0) + 1;
                    pic2danceAnalyzeSeqRef.current = seq;
                    const applyMeta = (meta) => {
                      if (pic2danceAnalyzeSeqRef.current !== seq) return;
                      const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                      const vw = Number((meta && meta.width) || 0);
                      const vh = Number((meta && meta.height) || 0);
                      if (!dur) { setPic2danceError(t('common.unsupportedFile')); if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.value = ''; return; }
                      if (dur > 30) { setPic2danceError(t('errors.videoTooLong30s')); if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.value = ''; return; }
                      if (vw > 0 && vh > 0) {
                        const longest = Math.max(vw, vh);
                        const defLongest = (longest >= 1080) ? '1080' : (longest >= 720 ? '720' : '576');
                        setPic2danceLongest(defLongest);
                      }
                      setPic2danceVideoFile(f);
                      setPic2danceDuration(dur);
                    };
                    analyzeVideoMetaWithMediaInfo(f)
                      .then((meta) => {
                        const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                        if (!dur) return Promise.reject(new Error('no duration'));
                        return meta;
                      })
                      .catch(() => analyzeVideoMetaWithVideoElement(f))
                      .catch(() => analyzeVideoMetaByContainerSniffing(f))
                      .then((meta) => applyMeta(meta))
                      .catch(() => { if (pic2danceAnalyzeSeqRef.current === seq) { setPic2danceError(t('common.unsupportedFile')); if (pic2danceVideoInputRef.current) pic2danceVideoInputRef.current.value = ''; } });
                  }}
                />
              </div>

              <div className="text-sm text-gray-800">{t('processing.pic2dance.optionsTitle')}</div>
              <div className="space-y-1">
                <label className="flex items-center space-x-2">
                  <input type="radio" name="pic2danceLongest" checked={pic2danceLongest==='1080'} onChange={()=>setPic2danceLongest('1080')} />
                  <span>{t('processing.pic2dance.longest1080')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="pic2danceLongest" checked={pic2danceLongest==='720'} onChange={()=>setPic2danceLongest('720')} />
                  <span>{t('processing.pic2dance.longest720')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="pic2danceLongest" checked={pic2danceLongest==='576'} onChange={()=>setPic2danceLongest('576')} />
                  <span>{t('processing.pic2dance.longest576')}</span>
                </label>
              </div>
              <div className="mt-2 text-xs text-gray-600 space-y-1">
                <div>{t('processing.pic2dance.noteResize')}</div>
                <div>{t('processing.pic2dance.noteFps')}</div>
                <div>{t('processing.pic2dance.noteLimit')}</div>
              </div>

              {pic2danceDuration > 0 && (
                <div className="flex flex-col gap-1 text-sm bg-gray-50 p-3 rounded border border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('processing.pic2dance.videoLabel')}</span>
                    <span className="font-medium">{pic2danceDuration} sec</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                    <span className="text-gray-800 font-bold">{t('processing.conv.pointsConsumption')}</span>
                    <span className="font-bold text-blue-600">{pic2dancePoints} pts</span>
                  </div>
                </div>
              )}
              {pic2danceBusy && (
                <div className="mt-2">
                  <div className="text-xs text-gray-700 mb-1">{t('processing.uploadProgress', { progress: pic2danceUploadProgress })}</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${pic2danceUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
              {pic2danceError && <div className="text-sm text-red-600">{pic2danceError}</div>}
              {pic2danceInsufficient && <div className="text-sm text-red-600 text-right">{t('pricing.insufficientPoints')}</div>}
              <div className="mt-4 flex justify-between items-center">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=>{ setShowPic2danceModal(false); resetPic2danceState(); }}>{t('common.close')}</button>
                <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowPic2danceModal(false); resetPic2danceState(); setShowPricing(true); }}>{t('pricing.buyPoints')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!pic2danceImageFile || !pic2danceVideoFile || !!pic2danceError || pic2danceDuration<=0 || pic2danceBusy || pic2danceInsufficient} onClick={async ()=>{
                  if (!pic2danceImageFile || !pic2danceVideoFile || pic2danceError || pic2danceDuration<=0 || pic2danceBusy) return;
                  setPic2danceBusy(true);
                  setPic2danceError('');
                  try {
                    const qopt = {
                      longestsize: String(pic2danceLongest || '1080'),
                      fps: 15
                    };

                    const uploadOne = (url, file, progressBase, progressSpan) => new Promise((resolve)=>{
                      const xhr = new XMLHttpRequest();
                      xhr.open('POST', url, true);
                      xhr.responseType = 'json';
                      xhr.withCredentials = true;
                      xhr.setRequestHeader('Content-Type','application/octet-stream');
                      xhr.upload.onprogress = (e)=>{
                        if (e.lengthComputable) {
                          const p = Math.min(100, Math.round(e.loaded * 100 / e.total));
                          setPic2danceUploadProgress(Math.min(100, progressBase + Math.round(p * (progressSpan / 100))));
                        }
                      };
                      xhr.onload = ()=> resolve({ ok: xhr.status>=200 && xhr.status<300, data: xhr.response });
                      xhr.onerror = ()=> resolve({ ok: false, data: { error: 'network error' } });
                      xhr.send(file);
                    });

                    setPic2danceUploadProgress(0);
                    const params1 = new URLSearchParams({
                      job_name: 'pic2dance',
                      file_name: pic2danceImageFile.name,
                      file_size: String(pic2danceImageFile.size),
                      file_timelength: String(pic2danceDuration),
                      point_used: String(pic2dancePoints),
                      file_in_options: JSON.stringify(qopt)
                    });
                    const up1 = await uploadOne(`${API_BASE}/api/auth/queue/upload?${params1.toString()}`, pic2danceImageFile, 0, 50);
                    const data1 = up1.data || {};
                    if (!(up1.ok && data1 && data1.ok)) {
                      let errMsg = String((data1 && data1.error) ? data1.error : t('errors.serverRegistrationFailed'));
                      if (errMsg === 'no points') errMsg = t('pricing.insufficientPoints');
                      setPic2danceError(errMsg);
                      setPic2danceBusy(false);
                      return;
                    }
                    const serial = Number(data1.job_serial || 0) || 0;
                    if (!serial) {
                      setPic2danceError(t('errors.serverRegistrationFailed'));
                      setPic2danceBusy(false);
                      return;
                    }
                    setPic2danceJobSerial(serial);

                    const params2 = new URLSearchParams({
                      job_serial: String(serial),
                      file_name: pic2danceVideoFile.name,
                      file_size: String(pic2danceVideoFile.size),
                      file_role: 'video'
                    });
                    const up2 = await uploadOne(`${API_BASE}/api/auth/queue/upload-extra?${params2.toString()}`, pic2danceVideoFile, 50, 50);
                    const data2 = up2.data || {};
                    if (up2.ok && data2 && data2.ok) {
                      setShowPic2danceModal(false);
                      setShowPic2danceSuccess(true);
                      resetPic2danceState();
                      if (typeof data1.points === 'number') {
                        setUser(prev => ({ ...(prev||{}), points: Number(data1.points||0) }));
                      } else {
                        try {
                          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
                          if (me.ok) {
                            const meData = await me.json();
                            setUser(prev => ({ ...(prev||{}), points: meData?.user?.points || (prev ? prev.points : 0) }));
                          }
                        } catch {}
                      }
                    } else {
                      let errMsg = String((data2 && data2.error) ? data2.error : t('errors.serverRegistrationFailed'));
                      if (errMsg === 'no points') errMsg = t('pricing.insufficientPoints');
                      setPic2danceError(errMsg);
                      setPic2danceBusy(false);
                    }
                  } catch {
                    setPic2danceError(t('errors.serverRegistrationFailed'));
                    setPic2danceBusy(false);
                  }
                }}>{t('processing.start')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFaceSwapModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[620px] text-gray-900">
            <div className="text-lg font-semibold mb-2">{t('features.faceswap.title')}</div>
            <div className="text-sm text-gray-700 mb-3">{t('features.faceswap.desc')}</div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700">{t('processing.faceswap.videoLabel')}</div>
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (faceSwapVideoInputRef.current) faceSwapVideoInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{faceSwapVideoFile ? String(faceSwapVideoFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input
                  ref={faceSwapVideoInputRef}
                  type="file"
                  className="hidden"
                  accept="video/*,.mp4,.mov,.mkv,.avi,.webm,.m4v,.ts,video/mp2t,video/MP2T"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setFaceSwapError('');
                    setFaceSwapVideoFile(null);
                    setFaceSwapDuration(0);
                    setFaceSwapPoints(0);
                    setFaceSwapUploadProgress(0);
                    setFaceSwapJobSerial(0);
                    if (!f) return;
                    if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setFaceSwapError(MAX_UPLOAD_ERROR); return; }
                    const name = (f.name || '').toLowerCase();
                    const allowed = ['.mp4', '.mov', '.mkv', '.avi', '.webm', '.m4v', '.ts'];
                    const ok = allowed.some(ext => name.endsWith(ext)) || String(f.type || '').startsWith('video/');
                    if (!ok) { setFaceSwapError(t('common.unsupportedFile')); if (faceSwapVideoInputRef.current) faceSwapVideoInputRef.current.value = ''; return; }

                    const seq = (faceSwapAnalyzeSeqRef.current || 0) + 1;
                    faceSwapAnalyzeSeqRef.current = seq;
                    const applyMeta = (meta) => {
                      if (faceSwapAnalyzeSeqRef.current !== seq) return;
                      const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                      if (!dur) { setFaceSwapError(t('common.unsupportedFile')); if (faceSwapVideoInputRef.current) faceSwapVideoInputRef.current.value = ''; return; }
                      setFaceSwapVideoFile(f);
                      setFaceSwapDuration(dur);
                    };
                    analyzeVideoMetaWithMediaInfo(f)
                      .then((meta) => {
                        const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                        if (!dur) return Promise.reject(new Error('no duration'));
                        return meta;
                      })
                      .catch(() => analyzeVideoMetaWithVideoElement(f))
                      .catch(() => analyzeVideoMetaByContainerSniffing(f))
                      .then((meta) => applyMeta(meta))
                      .catch(() => { if (faceSwapAnalyzeSeqRef.current === seq) { setFaceSwapError(t('common.unsupportedFile')); if (faceSwapVideoInputRef.current) faceSwapVideoInputRef.current.value = ''; } });
                  }}
                />
              </div>

              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700">{t('processing.faceswap.imageLabel')}</div>
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (faceSwapImageInputRef.current) faceSwapImageInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{faceSwapImageFile ? String(faceSwapImageFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input
                  ref={faceSwapImageInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setFaceSwapError('');
                    setFaceSwapImageFile(null);
                    if (!f) return;
                    if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setFaceSwapError(MAX_UPLOAD_ERROR); return; }
                    const name = (f.name || '').toLowerCase();
                    const allowed = ['.jpg', '.jpeg', '.png', '.webp'];
                    const ok = allowed.some(ext => name.endsWith(ext)) || String(f.type || '').startsWith('image/');
                    if (!ok) { setFaceSwapError(t('common.unsupportedFile')); if (faceSwapImageInputRef.current) faceSwapImageInputRef.current.value = ''; return; }
                    setFaceSwapImageFile(f);
                  }}
                />
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">{t('processing.faceswap.pointsTitle')}</div>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="fsfps" checked={faceSwapFps === '30'} onChange={()=>setFaceSwapFps('30')} className="form-radio text-blue-600" />
                    <span className="text-sm">{t('processing.faceswap.fps30')}</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="fsfps" checked={faceSwapFps === '15'} onChange={()=>setFaceSwapFps('15')} className="form-radio text-blue-600" />
                    <span className="text-sm">{t('processing.faceswap.fps15')}</span>
                  </label>
                </div>
              </div>

              {faceSwapDuration > 0 && (
                <div className="flex flex-col gap-1 text-sm bg-gray-50 p-3 rounded border border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('processing.faceswap.videoLabel')}</span>
                    <span className="font-medium">{faceSwapDuration} sec</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                    <span className="text-gray-800 font-bold">{t('processing.conv.pointsConsumption')}</span>
                    <span className="font-bold text-blue-600">{faceSwapPoints} pts</span>
                  </div>
                </div>
              )}
              {faceSwapBusy && (
                <div className="mt-2">
                  <div className="text-xs text-gray-700 mb-1">{t('processing.uploadProgress', { progress: faceSwapUploadProgress })}</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${faceSwapUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
              {faceSwapError && <div className="text-sm text-red-600">{faceSwapError}</div>}
              {(!!user && faceSwapPoints > 0 && userPoints < faceSwapPoints) && <div className="text-sm text-red-600 text-right">{t('pricing.insufficientPoints')}</div>}
              
              <div className="mt-4 flex justify-between items-center">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=>{ setShowFaceSwapModal(false); resetFaceSwapState(); }}>{t('common.close')}</button>
                <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowFaceSwapModal(false); resetFaceSwapState(); setShowPricing(true); }}>{t('pricing.buyPoints')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!faceSwapImageFile || !faceSwapVideoFile || !!faceSwapError || faceSwapDuration<=0 || faceSwapBusy || (!!user && faceSwapPoints > 0 && userPoints < faceSwapPoints)} onClick={async ()=>{
                  if (!faceSwapImageFile || !faceSwapVideoFile || faceSwapError || faceSwapDuration<=0 || faceSwapBusy) return;
                  setFaceSwapBusy(true);
                  setFaceSwapError('');
                  try {
                    const uploadOne = (url, file, progressBase, progressSpan) => new Promise((resolve)=>{
                      const xhr = new XMLHttpRequest();
                      xhr.open('POST', url, true);
                      xhr.responseType = 'json';
                      xhr.withCredentials = true;
                      xhr.setRequestHeader('Content-Type','application/octet-stream');
                      xhr.upload.onprogress = (e)=>{
                        if (e.lengthComputable) {
                          const p = Math.min(100, Math.round(e.loaded * 100 / e.total));
                          setFaceSwapUploadProgress(Math.min(100, progressBase + Math.round(p * (progressSpan / 100))));
                        }
                      };
                      xhr.onload = ()=> resolve({ ok: xhr.status>=200 && xhr.status<300, data: xhr.response });
                      xhr.onerror = ()=> resolve({ ok: false, data: { error: 'network error' } });
                      xhr.send(file);
                    });

                    setFaceSwapUploadProgress(0);
                    // Upload Image (Face) first
                    const params1 = new URLSearchParams({
                      job_name: 'faceswap',
                      file_name: faceSwapImageFile.name,
                      file_size: String(faceSwapImageFile.size),
                      file_timelength: String(faceSwapDuration),
                      point_used: String(faceSwapPoints),
                      file_in_options: JSON.stringify({ fps: Number(faceSwapFps) })
                    });
                    const up1 = await uploadOne(`${API_BASE}/api/auth/queue/upload?${params1.toString()}`, faceSwapImageFile, 0, 50);
                    const data1 = up1.data || {};
                    if (!(up1.ok && data1 && data1.ok)) {
                      let errMsg = String((data1 && data1.error) ? data1.error : t('errors.serverRegistrationFailed'));
                      if (errMsg === 'no points') errMsg = t('pricing.insufficientPoints');
                      setFaceSwapError(errMsg);
                      setFaceSwapBusy(false);
                      return;
                    }
                    const serial = Number(data1.job_serial || 0) || 0;
                    if (!serial) {
                      setFaceSwapError(t('errors.serverRegistrationFailed'));
                      setFaceSwapBusy(false);
                      return;
                    }
                    setFaceSwapJobSerial(serial);

                    // Upload Video second
                    const params2 = new URLSearchParams({
                      job_serial: String(serial),
                      file_name: faceSwapVideoFile.name,
                      file_size: String(faceSwapVideoFile.size),
                      file_role: 'video'
                    });
                    const up2 = await uploadOne(`${API_BASE}/api/auth/queue/upload-extra?${params2.toString()}`, faceSwapVideoFile, 50, 50);
                    const data2 = up2.data || {};
                    if (up2.ok && data2 && data2.ok) {
                      setShowFaceSwapModal(false);
                      setShowFaceSwapSuccess(true);
                      resetFaceSwapState();
                      if (typeof data1.points === 'number') {
                        setUser(prev => ({ ...(prev||{}), points: Number(data1.points||0) }));
                      } else {
                        try {
                          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
                          if (me.ok) {
                            const meData = await me.json();
                            setUser(prev => ({ ...(prev||{}), points: meData?.user?.points || (prev ? prev.points : 0) }));
                          }
                        } catch {}
                      }
                    } else {
                      let errMsg = String((data2 && data2.error) ? data2.error : t('errors.serverRegistrationFailed'));
                      if (errMsg === 'no points') errMsg = t('pricing.insufficientPoints');
                      setFaceSwapError(errMsg);
                      setFaceSwapBusy(false);
                    }
                  } catch {
                    setFaceSwapError(t('errors.serverRegistrationFailed'));
                    setFaceSwapBusy(false);
                  }
                }}>{t('processing.start')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFaceSwapSuccess && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('processing.reservedTitle')}</div>
            <div className="mt-2">{t('processing.reservedBody')}</div>
            <div className="mt-2">{t('processing.expiryNotice')}</div>
            <div className="mt-6">
              <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowFaceSwapSuccess(false); setShowQueueModal(true); }}>{t('processing.viewStatus')}</button>
            </div>
            <div className="mt-3">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowFaceSwapSuccess(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showSepSuccess && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('processing.reservedTitle')}</div>
            <div className="mt-2">{t('processing.reservedBody')}</div>
            <div className="mt-2">{t('processing.expiryNotice')}</div>
            <div className="mt-6">
              <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowSepSuccess(false); setShowQueueModal(true); }}>{t('processing.viewStatus')}</button>
            </div>
            <div className="mt-3">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowSepSuccess(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showPic2danceSuccess && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('processing.reservedTitle')}</div>
            <div className="mt-2">{t('processing.reservedBody')}</div>
            <div className="mt-2">{t('processing.expiryNotice')}</div>
            <div className="mt-6">
              <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowPic2danceSuccess(false); setShowQueueModal(true); }}>{t('processing.viewStatus')}</button>
            </div>
            <div className="mt-3">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowPic2danceSuccess(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showDenoiseModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('processing.denoise.title')}</div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (denoiseFileInputRef.current) denoiseFileInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{denoiseFile ? String(denoiseFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input ref={denoiseFileInputRef} type="file" className="hidden" accept="audio/*,video/*,.mp3,.wav,.mp4,.m4a,.aac,.ogg,.flac,.webm,.mov,.mkv,.avi,.m4v,.ts,video/mp2t,video/MP2T" onChange={async (e)=>{
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setDenoiseError(''); setDenoiseFile(null); setDenoiseDuration(0); setDenoisePoints(0); setDenoiseUploadProgress(0);
                if (!f) return;
                if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setDenoiseError(MAX_UPLOAD_ERROR); return; }
                const name = (f.name||'').toLowerCase();
                const allowed = ['.mp3','.wav','.mp4','.m4a','.aac','.ogg','.flac','.webm','.mov','.mkv','.avi','.m4v','.ts'];
                const ok = allowed.some(ext=>name.endsWith(ext)) || (String(f.type||'').startsWith('audio') || String(f.type||'').startsWith('video'));
                if (!ok) { setDenoiseError(t('common.unsupportedFile')); if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = ''; return; }
                const isAudio = String(f.type||'').startsWith('audio') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.aac') || name.endsWith('.flac') || name.endsWith('.ogg');
                if (isAudio) {
                  try {
                    const url = URL.createObjectURL(f);
                    const el = new Audio();
                    el.preload = 'metadata';
                    el.src = url;
                    await new Promise((resolve, reject)=>{
                      el.onloadedmetadata = ()=> resolve(null);
                      el.onerror = ()=> reject(new Error('metadata error'));
                    });
                    const dur = Math.max(0, Math.round(el.duration || 0));
                    URL.revokeObjectURL(url);
                    if (!dur) { setDenoiseError(t('common.unsupportedFile')); if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = ''; return; }
                    setDenoiseFile(f);
                    setDenoiseDuration(dur);
                    setDenoisePoints(dur);
                  } catch {
                    setDenoiseError(t('common.unsupportedFile'));
                    if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = '';
                  }
                  return;
                }

                const nameLower = String(f.name || '').toLowerCase();
                const seq = (denoiseAnalyzeSeqRef.current || 0) + 1;
                denoiseAnalyzeSeqRef.current = seq;
                const applyMeta = (meta) => {
                  if (denoiseAnalyzeSeqRef.current !== seq) return;
                  const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                  if (!dur) { setDenoiseError(t('common.unsupportedFile')); if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = ''; return; }
                  setDenoiseFile(f);
                  setDenoiseDuration(dur);
                  setDenoisePoints(dur);
                };
                analyzeVideoMetaWithMediaInfo(f)
                  .then((meta) => {
                    const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                    if (!dur) return Promise.reject(new Error('no duration'));
                    return meta;
                  })
                  .catch(() => analyzeVideoMetaWithVideoElement(f))
                  .catch(() => analyzeVideoMetaByContainerSniffing(f))
                  .then((meta) => applyMeta(meta))
                  .catch(() => { if (denoiseAnalyzeSeqRef.current === seq) { setDenoiseError(t('common.unsupportedFile')); if (denoiseFileInputRef.current) denoiseFileInputRef.current.value = ''; } });
                }} />
              </div>
              <div className="text-sm text-gray-700">{t('processing.pointsRate10')}</div>
              {denoiseDuration > 0 && (
                <div className="text-sm">{t('processing.lengthAndPoints', { seconds: denoiseDuration, points: denoisePoints })}</div>
              )}
              {denoiseBusy && (
                <div className="mt-2">
                  <div className="text-xs text-gray-700 mb-1">{t('processing.uploadProgress', { progress: denoiseUploadProgress })}</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${denoiseUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
              {denoiseError && <div className="text-sm text-red-600">{denoiseError}</div>}
              {denoiseInsufficient && <div className="text-sm text-red-600 text-right">{t('pricing.insufficientPoints')}</div>}
              <div className="mt-4 flex justify-between items-center">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=>{ setShowDenoiseModal(false); resetDenoiseState(); }}>{t('common.close')}</button>
                <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowDenoiseModal(false); resetDenoiseState(); setShowPricing(true); }}>{t('pricing.buyPoints')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!denoiseFile || !!denoiseError || denoiseDuration<=0 || denoiseBusy || denoiseInsufficient} onClick={async ()=>{
                  if (!denoiseFile || denoiseError || denoiseDuration<=0 || denoiseBusy) return;
                  setDenoiseBusy(true);
                  try {
                    const params = new URLSearchParams({
                      job_name: 'denoise',
                      file_name: denoiseFile.name,
                      file_size: String(denoiseFile.size),
                      file_timelength: String(denoiseDuration),
                      point_used: String(denoisePoints),
                      file_in_options: JSON.stringify({})
                    });
                    setDenoiseUploadProgress(0);
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE}/api/auth/queue/upload?${params.toString()}`, true);
                    xhr.responseType = 'json';
                    xhr.withCredentials = true;
                    xhr.setRequestHeader('Content-Type','application/octet-stream');
                    xhr.upload.onprogress = (e)=>{
                      if (e.lengthComputable) setDenoiseUploadProgress(Math.min(100, Math.round(e.loaded * 100 / e.total)));
                    };
                    const done = await new Promise((resolve)=>{
                      xhr.onload = ()=> resolve({ ok: xhr.status>=200 && xhr.status<300, data: xhr.response });
                      xhr.onerror = ()=> resolve({ ok: false, data: { error: 'network error' } });
                      xhr.send(denoiseFile);
                    });
                    const respOk = done.ok; const data = done.data || {};
                    if (respOk && data && data.ok) {
                      setShowDenoiseModal(false);
                      setShowDenoiseSuccess(true);
                      setDenoiseBusy(false);
                      setDenoiseUploadProgress(0);
                      setDenoiseFile(null);
                      setDenoiseDuration(0);
                      setDenoisePoints(0);
                      setDenoiseError('');
                      if (typeof data.points === 'number') {
                        setUser(prev => ({ ...(prev||{}), points: Number(data.points||0) }));
                      } else {
                        try {
                          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
                          if (me.ok) {
                            const meData = await me.json();
                            setUser(prev => ({ ...(prev||{}), points: meData?.user?.points || (prev ? prev.points : 0) }));
                          }
                        } catch {}
                      }
                    } else {
                      setDenoiseError(String((data && data.error) ? data.error : t('errors.serverRegistrationFailed')));
                      setDenoiseBusy(false);
                    }
                  } catch {
                    setDenoiseError(t('errors.serverRegistrationFailed'));
                    setDenoiseBusy(false);
                  }
                }}>{t('processing.start')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDenoiseSuccess && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('processing.reservedTitle')}</div>
            <div className="mt-2">{t('processing.reservedBody')}</div>
            <div className="mt-2">{t('processing.expiryNotice')}</div>
            <div className="mt-6">
              <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowDenoiseSuccess(false); setShowQueueModal(true); }}>{t('processing.viewStatus')}</button>
            </div>
            <div className="mt-3">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowDenoiseSuccess(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showConvModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[580px] text-gray-900">
            <div className="text-lg font-semibold mb-2">{t('processing.conv.title')}</div>
            <div className="text-sm text-gray-700 mb-3">{t('processing.conv.subtitle')}</div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <button type="button" className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm" onClick={() => { if (convFileInputRef.current) convFileInputRef.current.click(); }}>{t('common.selectFile')}</button>
                  <div className="text-sm text-gray-700 truncate">{convFile ? String(convFile.name || '') : t('common.noFileSelected')}</div>
                </div>
                <input ref={convFileInputRef} type="file" className="hidden" accept="video/*,.ts,video/mp2t,video/MP2T" onChange={async (e)=>{
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setConvError(''); setConvFile(null); setConvDuration(0); setConvPoints(0); setConvUploadProgress(0);
                if (!f) return;
                if (Number(f.size || 0) > MAX_UPLOAD_BYTES) { setConvError(MAX_UPLOAD_ERROR); if (convFileInputRef.current) convFileInputRef.current.value = ''; return; }
                const nameLower = String(f.name || '').toLowerCase();
                const seq = (convAnalyzeSeqRef.current || 0) + 1;
                convAnalyzeSeqRef.current = seq;
                const applyMeta = (meta) => {
                  if (convAnalyzeSeqRef.current !== seq) return;
                  const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                  const vw = Number((meta && meta.width) || 0);
                  const vh = Number((meta && meta.height) || 0);
                  if (!dur) { setConvError(t('common.unsupportedFile')); if (convFileInputRef.current) convFileInputRef.current.value = ''; return; }
                  const defPreset = (vw>=1920 || vh>=1080) ? '1080' : ((vw>0 && vh>0 && (vw<1024 || vh<576)) ? '576' : '720');
                  setConvPreset(defPreset);
                  setConvFile(f);
                  setConvDuration(dur);
                };
                analyzeVideoMetaWithMediaInfo(f)
                  .then((meta) => {
                    const dur = Math.max(0, Number((meta && meta.durationSec) || 0));
                    if (!dur) return Promise.reject(new Error('no duration'));
                    return meta;
                  })
                  .catch(() => analyzeVideoMetaWithVideoElement(f))
                  .catch(() => analyzeVideoMetaByContainerSniffing(f))
                  .then((meta) => applyMeta(meta))
                  .catch(() => { if (convAnalyzeSeqRef.current === seq) { setConvError(t('common.unsupportedFile')); if (convFileInputRef.current) convFileInputRef.current.value = ''; } });
                }} />
              </div>
              <div className="text-sm text-gray-800">{t('processing.conv.pointsConsumption')}</div>
              <div className="space-y-1">
                <label className="flex items-center space-x-2">
                  <input type="radio" name="convPreset" checked={convPreset==='1080'} onChange={()=>setConvPreset('1080')} />
                  <span>{t('processing.conv.rate1080')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="convPreset" checked={convPreset==='720'} onChange={()=>setConvPreset('720')} />
                  <span>{t('processing.conv.rate720')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="convPreset" checked={convPreset==='576'} onChange={()=>setConvPreset('576')} />
                  <span>{t('processing.conv.rate576')}</span>
                </label>
              </div>
              <div className="text-xs text-gray-700 mt-2">{t('processing.conv.noteResize')}</div>
              <div className="text-xs text-gray-700">{t('processing.conv.noteFps')}</div>
              <div className="text-xs text-gray-700">{t('processing.conv.noteUnit')}</div>
              <div className="mt-2 text-sm text-gray-800">{t('processing.conv.depthTitle')}</div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input type="radio" name="depthScale" checked={depthScale===40} onChange={()=>setDepthScale(40)} />
                  <span>{t('common.normal')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="radio" name="depthScale" checked={depthScale===80} onChange={()=>setDepthScale(80)} />
                  <span>{t('common.strong')}</span>
                </label>
              </div>
              {convDuration > 0 && (
                <div className="text-sm">{t('processing.lengthAndPoints', { seconds: convDuration, points: convPoints })}</div>
              )}
              {convFile && (
                <div className="mt-2">
                  <div className="text-xs text-gray-700 mb-1">{t('processing.uploadProgress', { progress: convUploadProgress })}</div>
                  <div className="h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-blue-600 rounded" style={{ width: `${convUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
              {convError && <div className="text-sm text-red-600">{convError}</div>}
              {convInsufficient && <div className="text-sm text-red-600 text-right">{t('pricing.insufficientPoints')}</div>}
              <div className="mt-4 flex justify-between items-center">
                <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=>{ setShowConvModal(false); resetConvState(); }}>{t('common.close')}</button>
                <button className="text-sm text-blue-700 hover:underline" onClick={()=>{ setShowConvModal(false); resetConvState(); setShowPricing(true); }}>{t('pricing.buyPoints')}</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!convFile || !!convError || convDuration<=0 || convBusy || convInsufficient} onClick={async ()=>{
                  if (!convFile || convError || convDuration<=0 || convBusy) return;
                  setConvBusy(true);
                  try {
                    const params = new URLSearchParams({
                      job_name: '2Dconv3D',
                      file_name: convFile.name,
                      file_size: String(convFile.size),
                      file_timelength: String(convDuration),
                      point_used: String(convPoints),
                      file_in_options: JSON.stringify(convPreset==='1080' ? {resX:1920,resY:1080,depth_scale:depthScale} : (convPreset==='720' ? {resX:1280,resY:720,depth_scale:depthScale} : {resX:1024,resY:576,depth_scale:depthScale}))
                    });
                    setConvUploadProgress(0);
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE}/api/auth/queue/upload?${params.toString()}`, true);
                    xhr.responseType = 'json';
                    xhr.withCredentials = true;
                    xhr.setRequestHeader('Content-Type','application/octet-stream');
                    xhr.upload.onprogress = (e)=>{
                      if (e.lengthComputable) setConvUploadProgress(Math.min(100, Math.round(e.loaded * 100 / e.total)));
                    };
                    const done = await new Promise((resolve)=>{
                      xhr.onload = ()=> resolve({ ok: xhr.status>=200 && xhr.status<300, data: xhr.response });
                      xhr.onerror = ()=> resolve({ ok: false, data: { error: 'network error' } });
                      xhr.send(convFile);
                    });
                    const respOk = done.ok; const data = done.data || {};
                    if (respOk && data && data.ok) {
                      setShowConvModal(false);
                      setShowSepSuccess(true);
                      setConvBusy(false);
                      setConvUploadProgress(0);
                      setConvFile(null);
                      setConvDuration(0);
                      setConvPoints(0);
                      setConvError('');
                      if (typeof data.points === 'number') {
                        setUser(prev => ({ ...(prev||{}), points: Number(data.points||0) }));
                      } else {
                        try {
                          const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
                          if (me.ok) {
                            const meData = await me.json();
                            setUser(prev => ({ ...(prev||{}), points: meData?.user?.points || (prev ? prev.points : 0) }));
                          }
                        } catch {}
                      }
                    } else {
                      setConvError(String((data && data.error) ? data.error : t('errors.serverRegistrationFailed')));
                      setConvBusy(false);
                    }
                  } catch {
                    setConvError(t('errors.serverRegistrationFailed'));
                    setConvBusy(false);
                  }
                }}>{t('processing.start')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQueueModal && (
        <QueueModal onClose={() => setShowQueueModal(false)} />
      )}

      {/* Email Login Modal */}
      {showEmailLogin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('auth.loginWithEmailTitle')}</div>
            <div className="space-y-3">
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.email')} className="w-full px-3 py-2 rounded border" />
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder={t('auth.password')} className="w-full px-3 py-2 rounded border" />
              <button onClick={emailLogin} className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">{t('auth.login')}</button>
            </div>
            <div className="mt-4 text-sm">
              <button className="text-blue-700 hover:underline" onClick={() => { setShowSignup(true); }}>{t('auth.signupFree')}</button>
              <div className="mt-2 text-gray-700">{t('auth.emailSignupNote')}</div>
            </div>
            {message && (
              <div className={`mt-2 text-sm ${message === USERNAME_AVAILABLE_MESSAGE ? 'text-blue-600' : 'text-red-600'}`}>{message}</div>
            )}
            <div className="text-center mt-4">
              <button className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setShowEmailLogin(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('auth.signupWithEmailTitle')}</div>
            <div className="space-y-3">
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder={t('auth.email')} className="w-full px-3 py-2 rounded border" />
              <button onClick={requestEmailSignup} className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">{t('auth.sendVerificationEmail')}</button>
            </div>
            <div className="mt-4 text-sm text-gray-700">{t('auth.emailSignupNote')}</div>
            {message && <div className="mt-2 text-sm text-green-700">{message}</div>}
            <div className="text-center mt-4">
              <button className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setShowSignup(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[520px] text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('auth.signupWithEmailVerifiedTitle')}</div>
            <div className="mb-2 text-sm text-gray-700">{verifiedEmail}</div>
            <div className="space-y-3">
              <div className="flex w-full space-x-2">
                <input value={username} onChange={e=>{ setUsername(e.target.value); setUsernamePolicyError(''); }} placeholder={t('auth.username')} className="flex-1 px-3 py-2 rounded border" />
                <button onClick={checkUsername} className="px-3 py-2 rounded bg-gray-700 hover:bg-gray-800 text-white">{t('auth.checkDuplicate')}</button>
              </div>
              {usernamePolicyError && <div className="text-red-600 text-sm">{usernamePolicyError}</div>}
              <input type="password" value={password} onChange={e=>{ setPassword(e.target.value); setPasswordMismatch(false); setPasswordPolicyError(''); }} placeholder={t('auth.password')} className="w-full px-3 py-2 rounded border" />
              <input type="password" value={password2} onChange={e=>{ setPassword2(e.target.value); setPasswordMismatch(false); }} placeholder={t('auth.confirmPassword')} className="w-full px-3 py-2 rounded border" />
              {passwordMismatch && <div className="text-red-600 text-sm">{t('auth.passwordMismatch')}</div>}
              <div className={`text-sm ${(usernamePolicyError || passwordPolicyError) ? 'text-red-600' : 'text-gray-700'}`}>{SIGNUP_POLICY_TEXT}</div>
              <button onClick={completeEmailSignup} className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded">{t('auth.register')}</button>
            </div>
            {message && (
              <div className={`mt-2 text-sm ${message === USERNAME_AVAILABLE_MESSAGE ? 'text-blue-600' : 'text-red-600'}`}>{message}</div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Sent Page */}
      {showSentPage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('auth.sentTitle')}</div>
            <div>{t('auth.sentBody')}</div>
          </div>
        </div>
      )}

      {/* Signup Completed Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('auth.signupCompleteTitle')}</div>
            <div>{t('auth.signupCompleteBody')}</div>
            {(selectedPlan && selectedPlan !== 'free') && (
              <div className="mt-2 text-sm text-gray-600">{t('auth.signupCompletePaidNote')}</div>
            )}
            <div className="mt-6">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowCompleteModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[3000]">
          <div className="bg-white rounded-lg p-8 w-[720px] max-h-[80vh] overflow-auto text-gray-900">
            <div className="text-2xl font-bold mb-6 text-blue-700">{t('legal.privacy.title')}</div>
            <div className="space-y-6 text-sm leading-relaxed">
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.collect.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.privacy.sections.collect.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.purpose.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.privacy.sections.purpose.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.dataHandling.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.dataHandling.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.thirdParty.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.thirdParty.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.security.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.security.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.externalServices.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.externalServices.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.changes.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.changes.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.sections.contact.title')}</div>
                <p className="text-gray-700">{t('legal.privacy.sections.contact.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.privacy.gdpr.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.privacy.gdpr.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
                <p className="text-gray-700 mt-2">{t('legal.privacy.gdpr.note')}</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowPrivacyModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[3000]">
          <div className="bg-white rounded-lg p-8 w-[720px] max-h-[80vh] overflow-auto text-gray-900">
            <div className="text-2xl font-bold mb-6">{t('legal.terms.title')}</div>
            <div className="space-y-6 text-sm leading-relaxed">
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.apply.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.apply.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.service.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.service.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.account.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.account.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.externalAuth.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.externalAuth.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.pointsFees.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.terms.sections.pointsFees.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.refund.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.refund.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.results.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.terms.sections.results.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.copyright.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.terms.sections.copyright.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.prohibited.title')}</div>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {t('legal.terms.sections.prohibited.items', { returnObjects: true }).map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.disclaimer.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.disclaimer.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.changes.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.changes.body')}</p>
              </div>
              <div>
                <div className="font-bold mb-2 text-gray-900">{t('legal.terms.sections.governingLaw.title')}</div>
                <p className="text-gray-700">{t('legal.terms.sections.governingLaw.body')}</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={()=> setShowTermsModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      {/* Subscription Result Modal */}
      {showSubModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('subscription.title')}</div>
            {(subStatus === 'ACTIVE') ? (
              <>
                <div className="mt-2">
                  {subPlanCode
                    ? t('subscription.changedPlanTo', { plan: (plans.find(p=>p.code===subPlanCode)?.name || subPlanCode) })
                    : (user ? t('subscription.pointsAvailable') : t('subscription.pleaseLogin'))}
                </div>
                <div className="mt-6">
                  <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowSubModal(false)}>{t('common.close')}</button>
                </div>
              </>
            ) : (
              <>
                <div>{t('subscription.retry')}</div>
                <div className="mt-6">
                  <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowSubModal(false)}>{t('common.close')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDowngradeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000]">
          <div className="bg-white rounded-lg p-8 w-[520px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('pricing.downgradedTitle')}</div>
            <div className="mt-2">{t('pricing.downgradedBody')}</div>
            <div className="mt-6">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowDowngradeModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showPaypalRedirectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[420px] text-gray-900 text-center">
            <div className="text-lg font-semibold mb-2">{t('paypal.redirectText')}</div>
          </div>
        </div>
      )}

      {showPricing && (
        <div className="fixed inset-0 bg-black/60 z-[2000] flex justify-center items-start sm:items-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-[920px] max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-y-auto text-gray-900">
            <div className="text-lg font-semibold mb-4">{t('pricing.title')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch">
              {plans.map(p => {
                const code = p.code;
                const name = p.name;
                const points = code === 'free' ? (p.initial_points || 0) : (p.points_sub_month || 0);
                const priceUsd = Number(p.price_usd_month || 0);
                const priceUsdLabel = Number.isFinite(priceUsd) ? (priceUsd % 1 === 0 ? String(Math.trunc(priceUsd)) : String(priceUsd)) : '';
                const note = (code === 'free' && p.initial_condition === 'google_only') ? t('pricing.freeResetNote') : '';
                const planCode = String(code || '').toLowerCase();
                const isFree = planCode === 'free';
                const isBasic = planCode === 'basic';
                const isPro = planCode === 'pro';
                const isUltra = planCode === 'ultra';
                const isKnownPlan = isFree || isBasic || isPro || isUltra;
                const hasPaidPrice = !isFree && Number.isFinite(priceUsd) && priceUsd > 0;
                const cardClassName = isKnownPlan
                  ? `bg-slate-900 p-6 rounded-3xl flex flex-col transition border ${isFree ? 'border-white/5 hover:border-blue-500/30' : isUltra ? 'border-white/10 hover:border-purple-500/30' : 'border-white/10 hover:border-blue-500/30'}`
                  : 'border rounded-lg p-4';
                const titleClassName = `text-lg font-bold mb-1 ${isFree ? 'text-slate-400' : isUltra ? 'text-purple-400' : 'text-blue-400'}`;
                const headerPrice = isFree ? t('pricing.free') : (hasPaidPrice ? `US$${priceUsdLabel}` : t('pricing.monthlyFree'));
                const displayPlanName = isKnownPlan ? (isFree ? t('pricing.planNames.free') : isBasic ? t('pricing.planNames.basic') : isPro ? t('pricing.planNames.pro') : t('pricing.planNames.ultra')) : name;
                const bulletItems = isKnownPlan
                  ? (isFree ? t('plans.bullets.free', { returnObjects: true })
                    : isBasic ? t('plans.bullets.basic', { returnObjects: true })
                    : isPro ? t('plans.bullets.pro', { returnObjects: true })
                    : t('plans.bullets.ultra', { returnObjects: true }))
                  : [];
                const checkColorClass = isUltra ? 'text-purple-500' : 'text-blue-500';
                const pointsNum = Number(points || 0);
                const pointsLabel = Number.isFinite(pointsNum) ? new Intl.NumberFormat(i18n.language === 'ja' ? 'ja-JP' : 'en-US').format(pointsNum) : String(points || '');
                const userPlanCode = user ? String(user.plan || '').toLowerCase() : '';
                const canChange = user && userPlanCode && userPlanCode !== planCode;
                const actionButtonClassName = 'w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-500/30 transition text-xs disabled:opacity-50 disabled:cursor-not-allowed';
                return (
                  <div key={code} className={cardClassName}>
                    {isKnownPlan ? (
                      <>
                        <div className="mb-6">
                          <h3 className={titleClassName}>{displayPlanName}</h3>
                          <div className="text-2xl font-extrabold text-white">
                            {headerPrice}
                            {hasPaidPrice && <span className="text-xs font-normal text-slate-500">{t('pricing.perMonthSuffix')}</span>}
                          </div>
                        </div>
                        <div className="mb-6 py-4 border-y border-white/10">
                          <div className={`flex items-center gap-2 font-bold ${isFree ? 'text-blue-400' : 'text-white'}`}>
                            <i className={`fas fa-coins ${isFree ? '' : 'text-yellow-500'}`} />
                            <span className="text-xl">{pointsLabel}</span>
                            <span className="text-[10px]">{t('pricing.pointsUnit')}</span>
                            {isFree && note && <span className="text-[10px] text-white">{t('pricing.noteMark1')}</span>}
                          </div>
                        </div>
                        <ul className={`space-y-3 mb-8 text-[13px] flex-grow ${isPro ? 'text-white' : 'text-slate-200'}`}>
                          {bulletItems.map((t) => (
                            <li key={t} className="flex items-center gap-2">
                              <span className={`${checkColorClass} text-[10px]`}>✓</span>
                              <span>{t}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold mb-2">{name}</div>
                        <div className="text-sm">{t('pricing.pointsLabel', { points })}</div>
                      </>
                    )}
                    {user ? (
                      canChange && (
                        <div className="mt-4">
                          <button className={actionButtonClassName} disabled={planChangeBusy===code} onClick={() => { setSelectedPlan(code); if (String(code || '').toLowerCase() !== 'free') setShowPricing(false); setPlanChangeBusy(code); changePlan(code); }}>{t('pricing.change')}</button>
                        </div>
                      )
                    ) : (
                      (code !== 'free') && (
                        <div className="mt-4">
                          <button className={actionButtonClassName} onClick={() => { setSelectedPlan(code); setMessage(''); setShowSignup(false); setShowEmailLogin(false); setShowSentPage(false); setShowPricing(false); setShowLoginModal(true); }}>{t('pricing.apply')}</button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-xs text-gray-700">{t('pricing.resetNotice')}</div>
            {(() => {
              const freePlan = plans.find(x => String(x?.code || '').toLowerCase() === 'free');
              const freeNote = freePlan && freePlan.initial_condition === 'google_only' ? t('pricing.freeResetNoteFull') : '';
              return freeNote ? <div className="mt-1 text-xs text-gray-700">{t('pricing.notePrefix1')}{freeNote}</div> : null;
            })()}
            {user && String(user.plan||'').toLowerCase() !== 'free' && (
              <div className="mt-4 p-3 border rounded">
                <div className="text-sm font-medium mb-2">{t('pricing.topupTitle')}</div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input type="number" min="1" value={topupUnits} onChange={e=>setTopupUnits(Number(e.target.value||1))} className="w-full sm:w-24 px-3 py-2 rounded border" />
                  <div className="text-sm">{t('pricing.topupHint')}</div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed" disabled={topupBusy} onClick={()=>{ setTopupBusy(true); createTopup(); }}>{t('pricing.buy')}</button>
                </div>
              </div>
            )}
            <div className="text-center mt-4">
              <button className="px-4 py-2 rounded bg-gray-800 text-white" onClick={() => setShowPricing(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="bg-slate-950 py-16 px-6 border-t border-white/5">
        <div className="w-full max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
          <div className="col-span-2">
            <div className="text-white mb-6 flex items-center gap-3">
              <img src={BRAND_LOGO} alt="MAKAREUS" className="w-9 h-9" />
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-none">MAKAREUS</span>
                <span className="text-xs text-slate-400 leading-none">{t('footer.tagline')}</span>
              </div>
            </div>
            <p className="text-slate-500 text-sm max-w-xs leading-relaxed">
              {t('footer.about')}
            </p>
          </div>
          <div>
            <h4 className="font-bold mb-6 text-white">{t('footer.links.title')}</h4>
            <ul className="text-slate-500 text-sm space-y-3">
              <li><a href={`${getHomePath()}#features`} className="hover:text-white transition">{t('footer.links.features')}</a></li>
              <li><button className="hover:text-white transition text-left" onClick={()=> setShowPricing(true)}>{t('pricing.nav')}</button></li>
              <li><a href={`${getHomePath()}#faq`} className="hover:text-white transition">{t('footer.links.faq')}</a></li>
              <li><button className="hover:text-white transition text-left" onClick={() => { if (user) { resetSupportState(); setShowSupportModal(true); } else { setShowLoginModal(true); } }}>{t('footer.links.support')}</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-6 text-white">{t('footer.legal.title')}</h4>
            <ul className="text-slate-500 text-sm space-y-3">
              <li><button className="hover:text-white transition text-left" onClick={()=> setShowPrivacyModal(true)}>{t('legal.privacy.title')}</button></li>
              <li><button className="hover:text-white transition text-left" onClick={()=> setShowTermsModal(true)}>{t('legal.terms.title')}</button></li>
              <li><a href={currentLng === 'ja' ? 'https://surgespace.com/about.html' : 'https://surgespace.com/en/about.html'} className="hover:text-white transition">{t('footer.legal.companyOverview')}</a></li>
            </ul>
          </div>
        </div>
        <div className="w-full max-w-7xl mx-auto mt-16 pt-8 border-t border-white/5 text-center text-slate-600 text-xs">
          {t('footer.copyrightPrefix', { year: new Date().getFullYear() })} <a href="https://surgespace.com" className="hover:text-slate-200 transition">SurgeSpace</a> {t('footer.copyrightSuffix')}
        </div>
      </footer>
    </div>
  );
}
